import React, { useState, useEffect, useRef } from "react";
import AnnouncementsFeed from "./AnnouncementsFeed";
import SoftReservePage from "./SoftReservePage";
import JoinRequestPage from "./JoinRequestPage";
import { API_URL } from "./config";
import CrossGuildInvitesHome from "./CrossGuildInvitesHome";
import OpenSeatsHome from "./OpenSeatsHome";
import { useToast } from "./Toast";
import { useConfirm } from "./ConfirmDialog";
import { TimezoneProvider, useTimezone } from "./lib/TimezoneContext";
import { getCurrentResetWeek, getNextResetWeek, localCellToBitIdx, resetRuleFrom } from "./lib/time";
import { DEFAULT_RESET_RULE, ResetRule } from "./lib/time";
import { formatWeekRange } from "./lib/regionReset";
import MobileRosterEditor from "./MobileRosterEditor";
import { shortGameName } from "./lib/gameName";
import {
  getClassColorFromFields,
  getSpecIconUrl,
  getClassIconUrl,
  resolveAssignedSpec,
  roleAccentColor,
  shortRole,
  getParseColor,
  stringToColor,
} from "./lib/classDisplay";

// --- TYPES ---
type Me = {
  id: string;
  discord_id: string;
  username: string;
  avatar: string | null;
  roles?: string[];
} | null;

type Tab = "home" | "schedule" | "chat" | "officer" | "profile";

interface Character {
  id: string;
  name: string;
  gameId: string;
  guildId?: string;
  guildName?: string;
  guildTag?: string;
  guildLogoUrl?: string;
  /** Asleep: kept, but out of lockouts, rosters, ARC and Discord pings. Set from desktop. */
  isSleeping?: boolean;
  /** Guildless-only opt-in to being picked up by any guild. See app_user_character.free_agent. */
  freeAgent?: boolean;
  options?: Array<{ group_id: number; option_id: number }>;
  metadata?: {
    class_name?: string;
    primary_role?: string;
    render_url?: string;
    wcl_perf?: { score?: number };
    realm?: string;
  };
}

interface SoftReserveInfo {
  enabled: boolean;
  max_reserves?: number;
  locked?: boolean;
  my_count?: number;
  my_items?: string[] | null;
}

interface ScheduledRaid {
  id: string;
  raidName: string;
  /** The raid's game. A guild can raid in more than one (Anniversary + WoW Forever). */
  gameId?: string | null;
  gameName?: string | null;
  localTimeLabel: string;
  hostedByGuild: string;
  myCharName?: string;
  myCharClass?: string;
  raidImageUrl?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  softReserveInfo?: SoftReserveInfo | null;
}

interface RaidChat {
  raid_id: string;
  raid_name: string;
  guild_name: string | null;
  guild_tag: string | null;
  next_session_time: string;
  character_name: string;
  unread_count: number;
}

interface ChatMessage {
  id: string;
  content: string;
  is_system: boolean;
  created_at: string;
  character_name: string;
  character_class: string;
  user_display_name: string;
  is_raid_leader: boolean;
}

/** A guild the signed-in user can actually administer (owner or officer). */
interface OfficerGuild {
  id: string;
  name: string;
  tag: string | null;
  logoUrl: string | null;
  isOwner: boolean;
}

/** Shape of GET /api/guilds/:guildId/readiness (officer-gated). */
interface GuildReadiness {
  currentWeek: string;
  nextWeek: string;
  /** The guild's reset rule. Absent from an older server, then EU. */
  resetRule?: ResetRule;
  players: number;
  officers: number;
  availabilitySet: number;
  availabilityConfirmed: number;
  upcomingRaids: number;
  discordConnected: boolean;
  releasedNextWeek: boolean;
  releasedCurrentWeek: boolean;
}

/** Shape of GET /guilds/:id/join-requests (pending only). */
interface JoinRequestRow {
  id: string;
  source: string;
  requestedAt: string;
  characterId: string | null;
  characterName: string | null;
  displayName: string | null;
  currentGuildName: string | null;
  className: string | null;
  primarySpecLabel: string | null;
  specLabels: string[];
  roleLabels: string[];
  wclScore: number | null;
  wclUrl: string | null;
  gameId?: string | null;
}

interface Option {
  id: number;
  key: string;
  label: string;
  image_url?: string | null;
  metadata?: {
    color?: string;
    role_color?: string;
    class_key?: string;
    role_key?: string;
    allowed_class_keys?: string[];
  };
}

interface FieldGroup {
  id: number;
  key: string;
  label: string;
  multiSelect: boolean;
  isRequired: boolean;
  options: Option[];
}

/**
 * Every active game's class/spec table, keyed by game id. A guild can play more
 * than one game (Anniversary + WoW Forever) and option ids differ per game, so a
 * character or raid is always drawn and edited with its OWN game's table.
 */
type FieldsByGame = Record<string, FieldGroup[]>;

function fieldsForGame(byGame: FieldsByGame, gameId?: string | null): FieldGroup[] {
  return (gameId != null && byGame[String(gameId)]) || [];
}

/** Small game chip, shown only where a list mixes games. */
function GameChip({ name }: { name?: string | null }) {
  if (!name) return null;
  return (
    <span className="shrink-0 rounded border border-cyan-400/30 bg-cyan-400/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-cyan-300">
      {name}
    </span>
  );
}

// --- ICONS ---
const Icons = {
  Home: () => (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  ),
  Schedule: () => (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  Chat: () => (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  ),
  Profile: () => (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  Officer: () => (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3l7 3v5.5c0 4.2-2.9 8.1-7 9.5-4.1-1.4-7-5.3-7-9.5V6l7-3z" />
    </svg>
  ),
  Back: () => (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  ),
  Send: () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
    </svg>
  ),
  Edit: () => (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  ),
  Trash: () => (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  ),
};

// --- UTILS ---
// Pick a vibrant, always-readable accent for a label (chat tiles, etc.).
// stringToColor can land on near-black; this palette stays bright on dark bg.
const ACCENT_PALETTE = ["#22d3ee", "#34d399", "#a78bfa", "#fbbf24", "#fb7185", "#38bdf8", "#fb923c", "#e879f9", "#2dd4bf", "#a3e635"];
function pickAccent(seed: string): string {
  if (!seed) return ACCENT_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  return ACCENT_PALETTE[Math.abs(hash) % ACCENT_PALETTE.length];
}

// Reset-week helpers now come from the shared implementation. These were a
// private copy of the "Wednesday 00:00 UTC" rule, which is EU-shaped and a day
// out for US realms. The default is EU, matching the previous behaviour.


// `weekStart` is a storage label (always a Wednesday), so it must be resolved to
// the week's real first day before display — a US week opens the Tuesday before.
function formatWeekLabel(weekStart: string, rule: ResetRule = DEFAULT_RESET_RULE): string {
  return formatWeekRange(weekStart, rule);
}

const MOBILE_AVAIL_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

const MOBILE_DAY_PARTS = [
  { label: "Morning", desc: "8am-12pm", start: 16, end: 24 },
  { label: "Afternoon", desc: "12pm-5pm", start: 24, end: 34 },
  { label: "Evening", desc: "7pm-11pm", start: 38, end: 46 },
] as const;

type MobileDayPart = (typeof MOBILE_DAY_PARTS)[number];

/** Local day-part half-hours → UTC-indexed availability bits (matches AvailabilityGrid). */
function mobileDayPartBitIndices(
  weekStartIso: string,
  dayLocalMon0: number,
  part: MobileDayPart,
  userTz: string
): number[] {
  const indices: number[] = [];
  for (let halfHour = part.start; halfHour < part.end; halfHour++) {
    indices.push(localCellToBitIdx(weekStartIso, dayLocalMon0, halfHour, userTz));
  }
  return indices;
}

function isMobileDayPartActive(
  availability: string,
  weekStartIso: string,
  dayLocalMon0: number,
  part: MobileDayPart,
  userTz: string
): boolean {
  for (const bitIdx of mobileDayPartBitIndices(weekStartIso, dayLocalMon0, part, userTz)) {
    if (availability[bitIdx] === "1") return true;
  }
  return false;
}

function toggleMobileDayPart(
  availability: string,
  weekStartIso: string,
  dayLocalMon0: number,
  part: MobileDayPart,
  userTz: string
): string {
  const chars = availability.split("");
  const bitIndices = mobileDayPartBitIndices(weekStartIso, dayLocalMon0, part, userTz);
  let activeCount = 0;
  for (const idx of bitIndices) {
    if (chars[idx] === "1") activeCount++;
  }
  const isTurningOn = activeCount < bitIndices.length / 2;
  for (const idx of bitIndices) {
    chars[idx] = isTurningOn ? "1" : "0";
  }
  return chars.join("");
}

// ============================================================================
// MOBILE SCHEDULE COMPONENT — Current/Next week tabs + confirmation
// ============================================================================
function MobileSchedule({ me }: { me: Me }) {
  const { showToast } = useToast();
  const timezone = useTimezone();

  type WeekData = { weekStart: string; slots: string; confirmedAt: string | null; isAutoCopied?: boolean };
  const [currentWeekData, setCurrentWeekData] = useState<WeekData | null>(null);
  const [nextWeekData, setNextWeekData] = useState<WeekData | null>(null);
  const [activeWeekTab, setActiveWeekTab] = useState<"current" | "next">("current");
  // This account's full reset rule from GET /me/availability, so the week
  // label names the real first day (Tuesday for US, Wednesday for EU).
  const [accountResetRule, setAccountResetRule] = useState<ResetRule>(DEFAULT_RESET_RULE);
  const [confirming, setConfirming] = useState(false);

  const activeData = activeWeekTab === "current" ? currentWeekData : nextWeekData;
  const availability = activeData?.slots || "0".repeat(336);

  const loadAvailability = () => {
    if (!me) return;
    fetch(`${API_URL}/me/availability`, { credentials: "include" })
      .then(res => res.ok ? res.json() : { slots: null })
      .then(data => {
        // The server already resolved this account's region; prefer its weeks
        // over recomputing them here with the EU default.
        // Use the server's FULL rule. Reconstructing one from the weekday alone
        // would assume 00:00 UTC and roll the week over early — 3 h for EU,
        // 15 h for US.
        const accountRule: ResetRule = data.resetRule ?? DEFAULT_RESET_RULE;
        setAccountResetRule(accountRule);
        const cw = getCurrentResetWeek(accountRule);
        const nw = getNextResetWeek(accountRule);

        if (data.currentWeek) {
          setCurrentWeekData({
            weekStart: data.currentWeek.weekStart || cw,
            slots: data.currentWeek.slots,
            confirmedAt: data.currentWeek.confirmedAt,
          });
        } else if (data.slots) {
          setCurrentWeekData({ weekStart: cw, slots: data.slots, confirmedAt: data.confirmedAt });
        }

        if (data.nextWeek) {
          setNextWeekData({
            weekStart: data.nextWeek.weekStart || nw,
            slots: data.nextWeek.slots,
            confirmedAt: data.nextWeek.confirmedAt,
            isAutoCopied: data.nextWeek.isAutoCopied,
          });
        } else if (data.slots) {
          setNextWeekData({ weekStart: nw, slots: data.slots, confirmedAt: null, isAutoCopied: true });
        }
      })
      .catch(err => console.error("Failed to load availability:", err));
  };

  useEffect(() => { loadAvailability(); }, [me]);

  const toggleDayPart = (dayIdx: number, part: MobileDayPart) => {
    if (!me || !activeData) return;

    const newString = toggleMobileDayPart(
      availability,
      activeData.weekStart,
      dayIdx,
      part,
      timezone
    );
    // Optimistically update local state
    if (activeWeekTab === "current") {
      setCurrentWeekData(prev => prev ? { ...prev, slots: newString, confirmedAt: new Date().toISOString() } : prev);
    } else {
      setNextWeekData(prev => prev ? { ...prev, slots: newString, confirmedAt: new Date().toISOString(), isAutoCopied: false } : prev);
    }

    fetch(`${API_URL}/me/availability`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slots: newString, weekStart: activeData.weekStart, confirm: true }),
      credentials: "include"
    })
      .then(res => { if (!res.ok) throw new Error("Save failed"); })
      .catch(() => { showToast("Failed to save", "error"); loadAvailability(); });
  };

  const handleConfirmSameAsLastWeek = async () => {
    if (!activeData || confirming) return;
    setConfirming(true);
    try {
      const res = await fetch(`${API_URL}/me/availability/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ weekStart: activeData.weekStart })
      });
      if (res.ok) {
        showToast("Availability confirmed!");
        loadAvailability();
      } else {
        showToast("Failed to confirm", "error");
      }
    } catch {
      showToast("Failed to confirm", "error");
    } finally {
      setConfirming(false);
    }
  };

  const isPartActive = (dayIdx: number, part: MobileDayPart) => {
    if (!activeData) return false;
    return isMobileDayPartActive(
      availability,
      activeData.weekStart,
      dayIdx,
      part,
      timezone
    );
  };

  const isConfirmed = !!activeData?.confirmedAt && !activeData?.isAutoCopied;

  if (!me) {
    return (
      <div className="relative bg-[#09090b] min-h-full text-white flex flex-col items-center justify-center p-6 pb-28 overflow-hidden">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-amber-500/10 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute bottom-1/4 right-0 w-48 h-48 bg-orange-500/8 rounded-full blur-[80px] pointer-events-none" />
        <div className="relative z-10 flex flex-col items-center text-center w-full max-w-xs">
          <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-amber-500/25 to-orange-600/10 border border-amber-500/25 flex items-center justify-center mb-6 shadow-2xl shadow-amber-500/10">
            <svg className="w-9 h-9 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-black text-white mb-2 tracking-tight">Your Schedule</h2>
          <p className="text-white/40 text-sm leading-relaxed mb-8">Set your raid availability once. Rallify does the rest. No spreadsheets, no chasing people.</p>
          <a href={`${API_URL}/auth/discord`} className="w-full py-4 bg-[#5865F2] hover:bg-[#4752C4] rounded-2xl font-bold text-white text-base flex items-center justify-center gap-3 shadow-lg shadow-[#5865F2]/25 transition-all hover:scale-[1.02] active:scale-[0.98]">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037 14.178 14.178 0 0 0-.624 1.29 18.423 18.423 0 0 0-4.825 0 14.12 14.12 0 0 0-.63-1.29.074.074 0 0 0-.077-.037 19.736 19.736 0 0 0-4.885 1.515.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.086 2.157 2.419 0 1.334-.956 2.42-2.157 2.42zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.086 2.157 2.419 0 1.334-.946 2.42-2.157 2.42z"/></svg>
            Continue with Discord
          </a>
          <p className="mt-4 text-[11px] text-white/25 tracking-wide">Free · No ads · Secure Discord login</p>
        </div>
      </div>
    );
  }

  const PART_ICON: Record<string, string> = { Morning: "☀️", Afternoon: "🌤️", Evening: "🌙" };

  return (
    <div className="pb-24 bg-[#09090b] min-h-full text-white">
      {/* Header — glow banner */}
      <div className="relative px-4 pt-8 pb-5 overflow-hidden">
        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-72 h-48 bg-cyan-500/12 rounded-full blur-[80px] pointer-events-none" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500/25 to-blue-600/10 border border-cyan-500/25 flex items-center justify-center shrink-0">
            <svg className="w-5 h-5 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
          </div>
          <div>
            <h2 className="text-2xl font-black tracking-tight leading-tight">Availability</h2>
            <p className="text-xs text-white/40">Tap to set when you can raid</p>
          </div>
        </div>
      </div>

      <div className="px-4">
        {/* Week Tabs */}
        <div className="flex gap-2 mb-3">
          {(["current", "next"] as const).map(tab => {
            const wd = tab === "current" ? currentWeekData : nextWeekData;
            const isActive = activeWeekTab === tab;
            const confirmed = wd?.confirmedAt && !wd?.isAutoCopied;
            return (
              <button
                key={tab}
                onClick={() => setActiveWeekTab(tab)}
                className={`flex-1 py-2.5 px-3 rounded-xl text-left transition-all border ${
                  isActive
                    ? "bg-cyan-600/20 border-cyan-500/50 shadow-lg shadow-cyan-500/10"
                    : "bg-white/5 border-white/10"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-black uppercase tracking-wider ${isActive ? "text-cyan-400" : "text-white/40"}`}>
                    {tab === "current" ? "This Week" : "Next Week"}
                  </span>
                  {confirmed ? (
                    <span className="text-[10px] bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded font-bold">✓</span>
                  ) : wd?.slots ? (
                    <span className="text-[10px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded font-bold">Unconfirmed</span>
                  ) : null}
                </div>
                {wd && (
                  <div className="text-[10px] text-white/30 mt-0.5">{formatWeekLabel(wd.weekStart, accountResetRule)}</div>
                )}
              </button>
            );
          })}
        </div>

        {/* Confirmation banner */}
        {activeData && !isConfirmed && activeData.slots?.includes("1") && (
          <button
            onClick={handleConfirmSameAsLastWeek}
            disabled={confirming}
            className="w-full mb-3 py-3 rounded-xl font-bold text-sm bg-green-600/20 border border-green-500/30 text-green-400 hover:bg-green-600/30 transition-colors disabled:opacity-50"
          >
            {confirming ? "Confirming..." : activeData.isAutoCopied ? "Confirm – Same as last week" : "Confirm Availability"}
          </button>
        )}
        {activeData && isConfirmed && (
          <div className="mb-3 py-2 px-3 rounded-xl text-xs text-green-400/80 bg-green-500/10 border border-green-500/20 text-center">
            ✓ Confirmed {new Date(activeData.confirmedAt!).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </div>
        )}

        {/* Day cards */}
        <div className="space-y-2.5">
          {MOBILE_AVAIL_DAYS.map((day, dIdx) => {
            const activeCount = MOBILE_DAY_PARTS.filter(p => isPartActive(dIdx, p)).length;
            return (
              <div key={day} className={`rounded-2xl border overflow-hidden transition-colors ${activeCount > 0 ? "border-cyan-500/25 bg-cyan-500/[0.04]" : "border-white/10 bg-white/[0.02]"}`}>
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="font-black text-sm uppercase tracking-wider text-white/70">{day}</span>
                  {activeCount > 0 && (
                    <span className="text-[10px] font-bold text-cyan-400 bg-cyan-500/15 px-1.5 py-0.5 rounded-full">{activeCount} set</span>
                  )}
                </div>
                <div className="px-2 pb-2 grid grid-cols-3 gap-2">
                  {MOBILE_DAY_PARTS.map((part) => {
                    const active = isPartActive(dIdx, part);
                    return (
                      <button
                        key={part.label}
                        onClick={() => toggleDayPart(dIdx, part)}
                        className={`py-3 px-2 rounded-xl text-center transition-all border ${
                          active
                            ? "bg-gradient-to-b from-cyan-500 to-cyan-600 border-cyan-400 text-white shadow-lg shadow-cyan-500/25"
                            : "bg-black/30 border-white/5 text-white/40 active:bg-white/5"
                        }`}
                      >
                        <div className={`text-lg leading-none mb-1 ${active ? "" : "grayscale opacity-60"}`}>{PART_ICON[part.label]}</div>
                        <div className="text-[11px] font-bold">{part.label}</div>
                        <div className="text-[9px] opacity-60">{part.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MOBILE CHAT COMPONENT - Fixed WebSocket subscription to match backend
// ============================================================================
function MobileChat({ me }: { me: Me }) {
  const { showToast } = useToast();
  const [raids, setRaids] = useState<RaidChat[]>([]);
  const [selectedRaid, setSelectedRaid] = useState<RaidChat | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!me) {
      setLoading(false);
      return;
    }
    fetch(`${API_URL}/raid-chat/my-raids`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        setRaids(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [me]);

  // Fetch messages when raid is selected
  useEffect(() => {
    if (!selectedRaid) return;
    
    fetch(`${API_URL}/raid-chat/${selectedRaid.raid_id}/messages?limit=100`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        setMessages(Array.isArray(data) ? data : []);
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
      });

    // Mark as read
    fetch(`${API_URL}/raid-chat/${selectedRaid.raid_id}/read`, {
      method: "PATCH",
      credentials: "include",
    });
  }, [selectedRaid]);

  // WebSocket connection - FIXED to match backend's expected message types
  useEffect(() => {
    if (!selectedRaid) return;
    
    let isSubscribed = false;

    const connectWebSocket = () => {
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const ws = new WebSocket(`${protocol}//${window.location.host}`);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("✅ WebSocket connected");
        setWsConnected(true);
        // FIXED: Use correct message type that backend expects
        ws.send(JSON.stringify({ type: "subscribe_raid", data: { raidId: selectedRaid.raid_id } }));
        isSubscribed = true;
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          
          if (msg.type === "subscribed") {
            console.log("✅ Subscribed to raid:", selectedRaid.raid_id);
          }

          // FIXED: Handle correct message type from backend
          if (msg.type === "new_message") {
            console.log("📨 New message received:", msg.data);
            setMessages(prev => [...prev, msg.data]);
            setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
          }

          if (msg.type === "message_deleted") {
            setMessages(prev => prev.filter(m => m.id !== msg.data.messageId));
          }
        } catch (e) {
          console.error("WebSocket message parse error:", e);
        }
      };

      ws.onerror = (error) => {
        console.error("❌ WebSocket error:", error);
        setWsConnected(false);
      };

      ws.onclose = () => {
        console.log("🔌 WebSocket disconnected");
        setWsConnected(false);
        isSubscribed = false;

        // Auto-reconnect after 2 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log("🔄 Reconnecting WebSocket...");
          connectWebSocket();
        }, 2000);
      };
    };

    connectWebSocket();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current?.readyState === 1 && isSubscribed) {
        wsRef.current.send(JSON.stringify({ type: "unsubscribe_raid", data: { raidId: selectedRaid.raid_id } }));
      }
      wsRef.current?.close();
    };
  }, [selectedRaid]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedRaid || sending) return;
    
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/raid-chat/${selectedRaid.raid_id}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: newMessage.trim() })
      });
      
      if (res.ok) {
        setNewMessage("");
        // Message will appear via WebSocket, but if WS is disconnected, add manually
        if (!wsConnected) {
          const data = await res.json();
          setMessages(prev => [...prev, data]);
          setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
        }
      } else {
        showToast("Failed to send message", "error");
      }
    } catch {
      showToast("Failed to send message", "error");
    } finally {
      setSending(false);
    }
  };

  if (!me) {
    return (
      <div className="relative h-full flex flex-col items-center justify-center p-6 pb-28 overflow-hidden bg-[#09090b]">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-cyan-500/10 rounded-full blur-[90px] pointer-events-none" />
        <div className="absolute top-1/4 right-0 w-40 h-40 bg-indigo-500/8 rounded-full blur-[70px] pointer-events-none" />
        <div className="relative z-10 flex flex-col items-center text-center w-full max-w-xs">
          <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-cyan-500/25 to-blue-600/10 border border-cyan-500/25 flex items-center justify-center mb-6 shadow-2xl shadow-cyan-500/10">
            <svg className="w-9 h-9 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <h2 className="text-2xl font-black text-white mb-2 tracking-tight">Raid Chat</h2>
          <p className="text-white/40 text-sm leading-relaxed mb-8">Coordinate with your team before and during every raid. Sign in to join the conversation.</p>
          <a href={`${API_URL}/auth/discord`} className="w-full py-4 bg-[#5865F2] hover:bg-[#4752C4] rounded-2xl font-bold text-white text-base flex items-center justify-center gap-3 shadow-lg shadow-[#5865F2]/25 transition-all hover:scale-[1.02] active:scale-[0.98]">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037 14.178 14.178 0 0 0-.624 1.29 18.423 18.423 0 0 0-4.825 0 14.12 14.12 0 0 0-.63-1.29.074.074 0 0 0-.077-.037 19.736 19.736 0 0 0-4.885 1.515.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.086 2.157 2.419 0 1.334-.956 2.42-2.157 2.42zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.086 2.157 2.419 0 1.334-.946 2.42-2.157 2.42z"/></svg>
            Continue with Discord
          </a>
          <p className="mt-4 text-[11px] text-white/25 tracking-wide">Free · No ads · Secure Discord login</p>
        </div>
      </div>
    );
  }

  // Chat view
  if (selectedRaid) {
    const headerAccent = pickAccent(selectedRaid.guild_tag || selectedRaid.guild_name || selectedRaid.raid_name);
    return (
      <div className="fixed inset-0 flex flex-col bg-[#09090b] z-50">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center gap-3 px-3 py-3 border-b border-white/10 bg-[#0f0f12]">
          <button onClick={() => setSelectedRaid(null)} className="p-1.5 -ml-1 rounded-lg active:bg-white/10 transition-colors">
            <Icons.Back />
          </button>
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center font-black text-sm shrink-0"
            style={{ backgroundColor: `${headerAccent}22`, color: headerAccent, border: `1px solid ${headerAccent}44` }}
          >
            {selectedRaid.guild_tag?.[0]?.toUpperCase() || selectedRaid.raid_name[0]?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-white truncate leading-tight">{selectedRaid.raid_name}</div>
            <div className="text-[11px] text-white/50 flex items-center gap-1.5">
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${wsConnected ? 'bg-green-400' : 'bg-red-400'}`} />
              {wsConnected ? "Connected" : "Reconnecting…"} · {selectedRaid.guild_name || "No Guild"}
            </div>
          </div>
        </div>

        {/* Messages - scrollable area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="text-3xl mb-2">👋</div>
              <div className="text-white/40 text-sm">No messages yet. Say hello!</div>
            </div>
          ) : (
            messages.map((msg) => {
              if (msg.is_system) {
                return (
                  <div key={msg.id} className="flex justify-center my-1">
                    <span className="text-[11px] text-white/40 italic bg-white/5 px-3 py-1 rounded-full">{msg.content}</span>
                  </div>
                );
              }
              const cc = stringToColor(msg.character_class);
              return (
                <div key={msg.id} className="flex gap-2.5">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-black shrink-0 mt-0.5"
                    style={{ backgroundColor: `${cc}22`, color: cc, border: `1px solid ${cc}44` }}
                  >
                    {msg.character_name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-bold text-sm truncate" style={{ color: cc }}>
                        {msg.character_name}
                      </span>
                      {msg.is_raid_leader && (
                        <span className="text-[9px] font-bold bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded shrink-0">RL</span>
                      )}
                      <span className="text-[10px] text-white/30 ml-auto shrink-0">
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div className="text-sm text-white/85 bg-white/5 rounded-2xl rounded-tl-md px-3 py-2 border border-white/5 break-words">
                      {msg.content}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input - FIXED at bottom with proper padding */}
        <div className="flex-shrink-0 p-3 border-t border-white/10 bg-[#0f0f12]">
          <div className="flex gap-2 items-center" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 8px)' }}>
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
              placeholder="Type a message..."
              className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-cyan-500/50"
            />
            <button
              onClick={sendMessage}
              disabled={!newMessage.trim() || sending}
              className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-cyan-600 rounded-full disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 transition-transform shadow-lg shadow-cyan-500/20"
            >
              <Icons.Send />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Raid list
  return (
    <div className="min-h-full bg-[#09090b] pb-24">
      {/* Header — glow banner */}
      <div className="relative px-4 pt-8 pb-5 overflow-hidden">
        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-72 h-48 bg-cyan-500/12 rounded-full blur-[80px] pointer-events-none" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500/25 to-blue-600/10 border border-cyan-500/25 flex items-center justify-center shrink-0">
            <svg className="w-5 h-5 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
          </div>
          <div>
            <h2 className="text-2xl font-black tracking-tight leading-tight">Raid Chats</h2>
            <p className="text-xs text-white/40">Your active raid discussions</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-white/40">Loading...</div>
      ) : raids.length === 0 ? (
        <div className="px-4">
          <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] py-12 text-center">
            <div className="text-3xl mb-2">💬</div>
            <div className="font-bold text-white/70">No active raid chats</div>
            <div className="text-xs text-white/30 mt-1">Join a raid to start chatting</div>
          </div>
        </div>
      ) : (
        <div className="px-4 space-y-2.5">
          {raids.map((raid) => {
            const accent = pickAccent(raid.guild_tag || raid.guild_name || raid.raid_name); // stable, readable per-raid color
            return (
              <button
                key={raid.raid_id}
                onClick={() => setSelectedRaid(raid)}
                className="group relative w-full p-3 text-left rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.05] to-white/[0.01] transition-transform active:scale-[0.99] flex items-center gap-3"
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center font-black text-lg shrink-0"
                  style={{ backgroundColor: `${accent}22`, color: accent, border: `1px solid ${accent}44` }}
                >
                  {raid.guild_tag?.[0]?.toUpperCase() || raid.raid_name[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-white truncate">{raid.raid_name}</div>
                  <div className="text-xs text-white/50 truncate mt-0.5">
                    {raid.guild_name || "No Guild"} · <span className="text-cyan-400/80">{raid.character_name}</span>
                  </div>
                </div>
                {raid.unread_count > 0 ? (
                  <div className="min-w-[24px] h-6 px-1.5 rounded-full bg-cyan-500 text-white text-xs font-black flex items-center justify-center shrink-0 shadow-lg shadow-cyan-500/30">
                    {raid.unread_count}
                  </div>
                ) : (
                  <svg className="w-4 h-4 text-white/20 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MOBILE HOME COMPONENT — With tappable raid detail (roster view)
// ============================================================================
function MobileHome({ me, raids, characters, fieldsByGame }: { me: Me; raids: ScheduledRaid[]; characters: Character[]; fieldsByGame: FieldsByGame }) {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [selectedRaidId, setSelectedRaidId] = useState<string | null>(null);
  const [raidDetail, setRaidDetail] = useState<any>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showSoftRes, setShowSoftRes] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [showRosterEditor, setShowRosterEditor] = useState(false);
  // Game chips only when the schedule mixes games (e.g. Anniversary + WoW Forever).
  const mixedGames = new Set(raids.map(r => r.gameId).filter(Boolean)).size > 1;

  const openRaidDetail = async (raidId: string) => {
    setSelectedRaidId(raidId);
    setLoadingDetail(true);
    setRaidDetail(null);
    setShowSoftRes(false);
    setShowRosterEditor(false);
    try {
      const res = await fetch(`${API_URL}/raids/${raidId}/detail`, { credentials: "include" });
      if (res.ok) {
        setRaidDetail(await res.json());
      }
    } catch (err) {
      console.error("Failed to load raid detail:", err);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Raider removes THEMSELVES from this raid (parity with desktop PlannerPage).
  // The server enforces both that only the caller's own characters are dropped
  // and that the raid hasn't started yet; we mirror its error codes below.
  const handleLeaveRaid = async () => {
    if (!selectedRaidId) return;
    const isConfirmed = await confirm({
      title: "Leave this raid?",
      description: "You'll be removed from the roster. If your guild's Discord bot is set up, your raid leaders will be notified.",
      confirmText: "Leave Raid",
      variant: "danger",
    });
    if (!isConfirmed) return;

    try {
      const res = await fetch(`${API_URL}/raids/${selectedRaidId}/leave`, { method: "POST", credentials: "include" });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.error === "raid_already_started") showToast("This raid has already started, so you can no longer remove yourself.", "error");
        else if (data.error === "not_assigned") showToast("You're not on this raid's roster.", "error");
        else showToast("Failed to leave the raid.", "error");
        return;
      }
      showToast("You've left the raid.", "success");
      openRaidDetail(selectedRaidId); // refresh detail so the roster reflects the change
    } catch {
      showToast("Network error. Please try again.", "error");
    }
  };

  // Raid Detail sub-view
  if (selectedRaidId) {
    const raidMeta = raids.find(r => r.id === selectedRaidId);
    const assignments: any[] = raidDetail?.assignments || [];
    // Class colors, icons and the roster editor use this raid's own game.
    const fields = fieldsForGame(fieldsByGame, raidDetail?.raid?.game_id || raidMeta?.gameId);

    // Full-screen Soft Reserve view (same page the desktop uses; it's responsive)
    if (showSoftRes && raidDetail?.raid) {
      const detailRaid = raidDetail.raid;
      const myAssignedChars = assignments
        .filter((a: any) => me && a.character_user_id === me.id)
        .map((a: any) => ({
          id: a.character_id,
          name: a.character_name,
          class_name: a.character_class_name || a.character_metadata?.class_name || null,
        }));
      const srChars = myAssignedChars.length > 0
        ? myAssignedChars
        : characters.map(c => ({ id: c.id, name: c.name, class_name: c.metadata?.class_name || null }));
      return (
        <div className="fixed inset-0 z-50 bg-[#09090b] overflow-y-auto overscroll-contain text-white">
          <SoftReservePage
            raidId={selectedRaidId}
            raidInfo={{
              id: selectedRaidId,
              name: detailRaid.name,
              guild_id: detailRaid.guild_id,
              game_id: detailRaid.game_id,
              raid_option_id: detailRaid.raid_option_id,
              raidName: raidMeta?.raidName || detailRaid.name,
              hostedByGuild: raidMeta?.hostedByGuild || detailRaid.guild_name,
              sessions: detailRaid.sessions || undefined,
            }}
            canManage={!!raidDetail.canManage}
            myCharacters={srChars}
            enrolledCharacterId={myAssignedChars[0]?.id}
            onBack={() => setShowSoftRes(false)}
          />
        </div>
      );
    }

    // Raid-day roster control. Officers, raid leaders and loot masters only
    // (the server decides via canManage; PUT /assignments re-checks).
    if (showRosterEditor && raidDetail?.raid) {
      return (
        <MobileRosterEditor
          raidId={selectedRaidId}
          raidName={raidMeta?.raidName || raidDetail.raid.name || "Raid"}
          raid={raidDetail.raid}
          initialAssignments={assignments}
          fields={fields}
          onClose={() => setShowRosterEditor(false)}
          onSaved={() => {
            setShowRosterEditor(false);
            openRaidDetail(selectedRaidId);
          }}
        />
      );
    }

    // Self-leave gate (mirrors desktop canSelfLeave): the current user can remove
    // themselves only while assigned AND before the raid's earliest session starts.
    // (Server re-enforces both.)
    const isAssignedSelf = !!me && assignments.some((a: any) => a.character_user_id === me.id);
    const raidSessions: any[] = raidDetail?.raid?.sessions || [];
    const earliestStartMs = raidSessions.length
      ? Math.min(...raidSessions
          .map((s: any) => new Date(s.starts_at).getTime())
          .filter((n: number) => !Number.isNaN(n)))
      : null;
    const raidNotStarted = earliestStartMs == null || earliestStartMs > Date.now();
    const canSelfLeave = isAssignedSelf && raidNotStarted;

    // Group by planner seat order (5 players per raid group), matching planner layout.
    const assignedToGroup = assignments
      .filter((a: any) => a.metadata?.seat_index !== undefined && a.metadata?.seat_index !== null)
      .sort((a: any, b: any) => Number(a.metadata.seat_index) - Number(b.metadata.seat_index));
    const ungroupedAssignments = assignments.filter(
      (a: any) => a.metadata?.seat_index === undefined || a.metadata?.seat_index === null
    );

    const groupedByRaidGroup = assignedToGroup.reduce((acc: Record<number, any[]>, assignment: any) => {
      const seatIdx = Number(assignment.metadata.seat_index);
      const groupNumber = Math.floor(seatIdx / 5) + 1;
      if (!acc[groupNumber]) acc[groupNumber] = [];
      acc[groupNumber].push(assignment);
      return acc;
    }, {});
    const raidGroupNumbers = Object.keys(groupedByRaidGroup)
      .map(Number)
      .sort((a, b) => a - b);

    const renderPlayer = (a: any) => {
      const className = a.character_class_name || a.character_metadata?.class_name || "Unknown";
      const classColor = getClassColorFromFields(className, fields);
      const isPug = a.metadata?.is_pug;
      const specLabel = resolveAssignedSpec(a);
      const roleColor = roleAccentColor(a.role_label);
      const iconUrl = getSpecIconUrl(specLabel, className, fields) || getClassIconUrl(className, fields);
      return (
        <div
          key={a.character_id}
          className="relative flex items-center gap-3 py-2.5 pl-3.5 pr-2.5 rounded-xl bg-gradient-to-r from-white/[0.05] to-white/[0.01] border border-white/5 overflow-hidden"
        >
          {/* role accent bar */}
          <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: roleColor }} />

          {/* spec / class icon */}
          <div
            className="relative w-9 h-9 rounded-lg overflow-hidden shrink-0"
            style={{ border: `1px solid ${classColor}55`, backgroundColor: `${classColor}1a` }}
          >
            {iconUrl ? (
              <img
                src={iconUrl}
                alt={specLabel}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const el = e.currentTarget;
                  el.style.display = "none";
                  const fb = el.nextElementSibling as HTMLElement | null;
                  if (fb) fb.style.display = "flex";
                }}
              />
            ) : null}
            <span
              className="absolute inset-0 items-center justify-center text-xs font-black"
              style={{ display: iconUrl ? "none" : "flex", color: classColor }}
            >
              {a.character_name?.[0]?.toUpperCase() || "?"}
            </span>
          </div>

          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm truncate" style={{ color: classColor }}>
              {a.character_name}
              {isPug && <span className="text-[10px] text-amber-400 ml-1.5 font-normal">(PUG)</span>}
            </div>
            <div className="text-[11px] font-medium truncate" style={{ color: roleColor }}>
              {specLabel || className}
            </div>
          </div>

          {/* role tag */}
          <span
            className="shrink-0 text-[9px] font-black uppercase tracking-wider px-2 py-1 rounded-md"
            style={{ color: roleColor, backgroundColor: `${roleColor}1a` }}
          >
            {shortRole(a.role_label)}
          </span>
        </div>
      );
    };

    const renderGroup = (label: string, items: any[], accent: "cyan" | "amber", capacity?: number) => {
      if (items.length === 0) return null;
      const isAmber = accent === "amber";
      return (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
          <div className={`flex items-center justify-between px-3.5 py-2.5 border-b border-white/5 bg-gradient-to-r ${isAmber ? "from-amber-500/10" : "from-cyan-500/10"} to-transparent`}>
            <div className="flex items-center gap-2">
              <span className={`flex items-center justify-center w-6 h-6 rounded-md text-[11px] font-black ${isAmber ? "bg-amber-500/20 text-amber-300" : "bg-cyan-500/20 text-cyan-300"}`}>
                {isAmber ? "★" : label.replace(/\D/g, "") || "•"}
              </span>
              <span className="text-xs font-bold uppercase tracking-wider text-white/70">{label}</span>
            </div>
            <span className="text-[10px] font-bold text-white/30">
              {capacity ? `${items.length}/${capacity}` : items.length}
            </span>
          </div>
          <div className="p-2 space-y-1.5">{items.map(renderPlayer)}</div>
        </div>
      );
    };

    return (
      <div className="pb-24 bg-[#09090b] min-h-full text-white">
        {/* Cinematic header */}
        <div className="relative h-44 overflow-hidden">
          {raidMeta?.raidImageUrl ? (
            <img src={raidMeta.raidImageUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/50 to-indigo-900/50" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-[#09090b]/55 to-[#09090b]/20" />

          {/* Back button */}
          <button
            onClick={() => setSelectedRaidId(null)}
            className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/50 border border-white/15 backdrop-blur-sm text-white/80 active:scale-95 transition-transform"
          >
            <Icons.Back /> <span className="text-xs font-bold">Back</span>
          </button>

          {/* Title */}
          <div className="absolute bottom-0 inset-x-0 p-4">
            <h2 className="text-2xl font-black text-white leading-tight drop-shadow-lg mb-1">{raidMeta?.raidName || "Raid"}</h2>
            <div className="text-sm font-medium text-cyan-300">
              {raidMeta?.localTimeLabel} · {raidMeta?.hostedByGuild}
            </div>
            {mixedGames && raidMeta?.gameName && <div className="mt-1.5"><GameChip name={raidMeta.gameName} /></div>}
          </div>
        </div>

        {/* Body */}
        <div className="p-4">
          {loadingDetail ? (
            <div className="py-12 text-center text-white/40">Loading roster...</div>
          ) : assignments.length === 0 ? (
            <div className="py-12 text-center text-white/40">No players assigned yet</div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-xs text-white/30 mb-1">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 100-8 4 4 0 000 8z" /></svg>
                <span className="font-bold">{assignments.length} players</span>
              </div>

              {/* Class & role makeup — parity with the desktop planner summary bar. Counts seated players only. */}
              {assignedToGroup.length > 0 && (() => {
                const roleCounts = { Tank: 0, Healer: 0, Melee: 0, Ranged: 0 };
                const classCounts: Record<string, number> = {};
                assignedToGroup.forEach((a: any) => {
                  const r = (a.role_label || "").toLowerCase();
                  if (r.includes("tank")) roleCounts.Tank++;
                  else if (r.includes("heal")) roleCounts.Healer++;
                  else if (r.includes("ranged")) roleCounts.Ranged++;
                  else roleCounts.Melee++;
                  const cn = a.character_class_name || a.character_metadata?.class_name || "Unknown";
                  classCounts[cn] = (classCounts[cn] || 0) + 1;
                });
                const sortedClasses = Object.entries(classCounts).sort((a, b) => b[1] - a[1]);
                const seated = assignedToGroup.length;
                const capacity = Number(raidDetail?.raid?.metadata?.size) || Number((raidMeta as any)?.size) || 0;
                const roleChips: [string, number][] = [
                  ["🛡️", roleCounts.Tank],
                  ["💚", roleCounts.Healer],
                  ["⚔️", roleCounts.Melee],
                  ["🏹", roleCounts.Ranged],
                ];
                return (
                  <div className="rounded-2xl border border-white/10 bg-white/[0.02] px-3 py-2.5 flex items-center gap-2 overflow-x-auto">
                    {/* Role counts */}
                    <div className="flex items-center gap-1.5 shrink-0">
                      {roleChips.map(([icon, count]) => (
                        <span key={icon} className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-white/5 border border-white/10 text-[11px]">
                          <span>{icon}</span><span className="font-bold text-white">{count}</span>
                        </span>
                      ))}
                    </div>

                    <span className="text-white/10 shrink-0">|</span>

                    {/* Class breakdown */}
                    <div className="flex items-center gap-1 shrink-0">
                      {sortedClasses.map(([className, count]) => {
                        const color = getClassColorFromFields(className, fields);
                        return (
                          <span key={className} className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-black/40 border border-white/5 text-[11px]" title={className}>
                            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                            <span className="font-bold" style={{ color }}>{count}</span>
                          </span>
                        );
                      })}
                    </div>

                    {/* Total */}
                    <span className="text-[11px] text-white/30 font-medium ml-auto pl-2 shrink-0">
                      {seated}{capacity ? `/${capacity}` : ""}
                    </span>
                  </div>
                );
              })()}

              {raidGroupNumbers.map((groupNum) =>
                renderGroup(`Group ${groupNum}`, groupedByRaidGroup[groupNum], "cyan", 5)
              )}
              {renderGroup("Bench / Ungrouped", ungroupedAssignments, "amber")}
            </div>
          )}

          {/* Raid-day roster control, for whoever can actually manage this raid */}
          {raidDetail?.canManage && raidDetail?.raid && (
            <button
              onClick={() => setShowRosterEditor(true)}
              className="mt-5 w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 font-bold text-sm active:scale-[0.98] transition-transform"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 100-8 4 4 0 000 8z" /></svg>
              Manage Roster
            </button>
          )}

          {/* Soft Reserves: full SR flow, same as desktop */}
          {raidDetail?.raid && (() => {
            const srInfo = raidMeta?.softReserveInfo;
            return (
              <button
                onClick={() => setShowSoftRes(true)}
                className="mt-5 w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-300 font-bold text-sm active:scale-[0.98] transition-transform"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                Soft Reserves
                {srInfo?.enabled && (
                  <span className={`ml-1 text-[11px] font-black px-2 py-0.5 rounded-full ${
                    (srInfo.my_count || 0) >= (srInfo.max_reserves || 2)
                      ? "bg-emerald-500/20 text-emerald-300"
                      : "bg-amber-500/20 text-amber-200"
                  }`}>
                    {srInfo.my_count || 0}/{srInfo.max_reserves || 2}
                  </span>
                )}
                {srInfo?.enabled && srInfo.locked && <span className="text-[11px]">🔒</span>}
              </button>
            );
          })()}

          {/* Self-leave: only when the user is on the roster AND the raid hasn't started */}
          {canSelfLeave && (
            <button
              onClick={handleLeaveRaid}
              className="mt-5 w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-red-500/10 border border-red-500/30 text-red-300 font-bold text-sm active:scale-[0.98] transition-transform"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
              Leave Raid
            </button>
          )}
        </div>
      </div>
    );
  }

  // --- LOGGED OUT: full-screen hero, glow bleeds into the page (matches other tabs) ---
  if (!me) {
    return (
      <div className="relative bg-[#09090b] min-h-full flex flex-col overflow-hidden">
        {/* Ambient glow bleeding into the background */}
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[130%] h-80 bg-cyan-500/15 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute top-1/4 -right-20 w-64 h-64 bg-indigo-500/12 rounded-full blur-[90px] pointer-events-none" />
        <div className="absolute top-1/3 -left-20 w-56 h-56 bg-blue-500/10 rounded-full blur-[80px] pointer-events-none" />

        {/* Hero */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-8 pt-16 pb-10">
          <div className="w-24 h-24 rounded-[28px] bg-gradient-to-br from-cyan-500 to-blue-700 flex items-center justify-center font-black text-5xl text-white shadow-2xl shadow-cyan-500/40 ring-1 ring-white/20 mb-8">
            R
          </div>
          <div className="text-[11px] text-cyan-400 font-black uppercase tracking-[0.3em] mb-3">Rallify</div>
          <h1 className="text-4xl font-black text-white tracking-tight leading-[1.05] mb-4">
            Less admin.<br />
            <span className="bg-gradient-to-r from-cyan-400 via-cyan-300 to-indigo-400 bg-clip-text text-transparent">More play.</span>
          </h1>
          <p className="text-white/40 text-sm leading-relaxed mb-9 max-w-[270px]">
            Everyone sets their time once. Rallify builds a complete, balanced raid in seconds.
          </p>
          <a href={`${API_URL}/auth/discord`} className="w-full max-w-[300px] py-4 bg-[#5865F2] hover:bg-[#4752C4] rounded-2xl font-bold text-white text-base flex items-center justify-center gap-3 shadow-lg shadow-[#5865F2]/30 transition-all hover:scale-[1.02] active:scale-[0.98]">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037 14.178 14.178 0 0 0-.624 1.29 18.423 18.423 0 0 0-4.825 0 14.12 14.12 0 0 0-.63-1.29.074.074 0 0 0-.077-.037 19.736 19.736 0 0 0-4.885 1.515.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.086 2.157 2.419 0 1.334-.956 2.42-2.157 2.42zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.086 2.157 2.419 0 1.334-.946 2.42-2.157 2.42z"/></svg>
            Continue with Discord
          </a>
          <p className="mt-4 text-[11px] text-white/25 tracking-wide">Free · No ads · Secure Discord login</p>
        </div>

        {/* Announcements — anchored in a card so it reads as intentional, not floating */}
        <div className="relative z-10 px-4 pb-24">
          <div className="rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur-sm p-4">
            <h3 className="text-[11px] font-bold uppercase text-white/40 mb-3 tracking-[0.15em] flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" /> Announcements
            </h3>
            <AnnouncementsFeed />
          </div>
        </div>
      </div>
    );
  }

  const classColorFor = (raid: ScheduledRaid) =>
    getClassColorFromFields(raid.myCharClass || "Unknown", fieldsForGame(fieldsByGame, raid.gameId));
  const dateBadge = (iso?: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    return {
      month: d.toLocaleDateString(undefined, { month: "short" }).toUpperCase(),
      day: String(d.getDate()),
      weekday: d.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase(),
    };
  };
  // Split active (upcoming or happening now) vs past, mirroring the desktop planner:
  // a raid is past once its last session has ended (start + duration).
  const nowMs = Date.now();
  const isPastRaid = (r: ScheduledRaid) => !!r.endsAt && new Date(r.endsAt).getTime() <= nowMs;
  const isLiveRaid = (r: ScheduledRaid) =>
    !!r.startsAt && !!r.endsAt &&
    nowMs >= new Date(r.startsAt).getTime() && nowMs < new Date(r.endsAt).getTime();
  const activeRaids = raids.filter(r => !isPastRaid(r));
  const pastRaids = raids.filter(isPastRaid).slice().reverse(); // most recent first

  const nextRaid = activeRaids[0];
  const restRaids = activeRaids.slice(1);

  const renderCompactRaidCard = (raid: ScheduledRaid, dimmed = false) => {
    const b = dateBadge(raid.startsAt);
    const cc = classColorFor(raid);
    return (
      <button
        key={raid.id}
        onClick={() => openRaidDetail(raid.id)}
        className={`group relative flex items-center w-full h-24 rounded-xl overflow-hidden border border-white/10 bg-[#0E1015] text-left transition-all active:scale-[0.99] ${dimmed ? "opacity-50 grayscale" : ""}`}
      >
        {/* bg art fading in from the right */}
        {raid.raidImageUrl && (
          <div
            className="absolute inset-y-0 right-0 w-2/3"
            style={{ maskImage: 'linear-gradient(to right, transparent 0%, black 65%)', WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 65%)' }}
          >
            <img src={raid.raidImageUrl} alt="" className="h-full w-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0E1015] via-[#0E1015]/70 to-transparent" />

        {/* Date badge */}
        {b && (
          <div className="relative z-10 flex flex-col items-center justify-center w-16 shrink-0">
            <span className="text-[9px] font-bold uppercase tracking-widest text-white/40 leading-none">{b.month}</span>
            <span className="text-2xl font-black text-white leading-tight">{b.day}</span>
            <span className={`text-[9px] font-bold uppercase leading-none ${dimmed ? "text-white/40" : "text-cyan-400/80"}`}>{b.weekday}</span>
          </div>
        )}

        {/* Info */}
        <div className="relative z-10 flex-1 min-w-0 pr-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-black text-white truncate">{raid.raidName}</span>
            {mixedGames && <GameChip name={raid.gameName} />}
          </div>
          <div className="text-[11px] text-white/50 mt-0.5 truncate">{raid.localTimeLabel} · {raid.hostedByGuild}</div>
          <div className="flex items-center gap-2 mt-1">
            {raid.myCharName && (
              <span className="flex items-center gap-1.5 min-w-0">
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: cc }} />
                <span className="text-[11px] font-bold truncate" style={{ color: cc }}>{raid.myCharName}</span>
              </span>
            )}
            {!dimmed && raid.softReserveInfo?.enabled && (
              <span className={`shrink-0 text-[9px] font-black px-1.5 py-0.5 rounded ${
                (raid.softReserveInfo.my_count || 0) >= (raid.softReserveInfo.max_reserves || 2)
                  ? "bg-emerald-500/15 text-emerald-300"
                  : "bg-amber-500/15 text-amber-300"
              }`}>
                SR {raid.softReserveInfo.my_count || 0}/{raid.softReserveInfo.max_reserves || 2}
              </span>
            )}
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="p-4 space-y-6 pb-24 bg-[#09090b] min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-700 flex items-center justify-center font-black text-lg text-white shadow-lg shadow-cyan-500/20 ring-1 ring-white/10 shrink-0">
            R
          </div>
          <div className="min-w-0">
            <div className="text-[10px] text-cyan-400 font-bold uppercase tracking-[0.2em]">Rallify</div>
            <div className="font-black text-xl text-white tracking-tight truncate leading-tight">
              Hey, {me.username.split('#')[0]}
            </div>
          </div>
        </div>
        {activeRaids.length > 0 && (
          <div className="text-right shrink-0">
            <div className="text-2xl font-black text-cyan-400 leading-none">{activeRaids.length}</div>
            <div className="text-[9px] uppercase tracking-wider text-white/40 mt-0.5">raids</div>
          </div>
        )}
      </div>

      {/* Cross-Guild Raid Invitations — only renders if there are pending invites */}
      <CrossGuildInvitesHome isAuthed={!!me} variant="mobile" />

      {/* Open seats: public seats in raids this player qualifies for */}
      <OpenSeatsHome variant="mobile" />

      {/* NEXT RAID — cinematic hero using the raid's background art */}
      {nextRaid && (() => {
        const b = dateBadge(nextRaid.startsAt);
        const cc = classColorFor(nextRaid);
        const live = isLiveRaid(nextRaid);
        return (
          <button
            onClick={() => openRaidDetail(nextRaid.id)}
            className="group relative block w-full h-56 rounded-2xl overflow-hidden border border-white/10 text-left transition-transform active:scale-[0.99]"
          >
            {nextRaid.raidImageUrl ? (
              <img src={nextRaid.raidImageUrl} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/50 to-indigo-900/50" />
            )}
            {/* readability overlays */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-[#09090b]/55 to-[#09090b]/10" />
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-transparent" />

            {/* Next-up / Live badge */}
            <div className={`absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full shadow-lg ${live ? "bg-red-500 shadow-red-500/40" : "bg-cyan-500 shadow-cyan-500/40"}`}>
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
              </span>
              <span className="text-[10px] font-black uppercase tracking-wider text-white">{live ? "Live Now" : "Next Up"}</span>
            </div>

            {/* Date badge */}
            {b && (
              <div className="absolute top-3 right-3 flex flex-col items-center justify-center rounded-xl bg-black/50 border border-white/15 px-3 py-1.5 backdrop-blur-sm">
                <span className="text-[9px] font-bold uppercase tracking-widest text-white/50 leading-none">{b.month}</span>
                <span className="text-xl font-black text-white leading-tight my-0.5">{b.day}</span>
                <span className="text-[9px] font-bold text-white/50 uppercase leading-none">{b.weekday}</span>
              </div>
            )}

            {/* Content */}
            <div className="absolute bottom-0 inset-x-0 p-4">
              <div className="text-[11px] font-bold text-cyan-300 uppercase tracking-wider mb-1">
                {nextRaid.localTimeLabel} · {nextRaid.hostedByGuild}
              </div>
              <h2 className="text-2xl font-black text-white leading-tight drop-shadow-lg mb-2.5">{nextRaid.raidName}</h2>
              {mixedGames && nextRaid.gameName && <div className="-mt-1 mb-2.5"><GameChip name={nextRaid.gameName} /></div>}
              <div className="flex items-center gap-2 flex-wrap">
                {nextRaid.myCharName && (
                  <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-lg bg-black/40 border border-white/10 backdrop-blur-sm">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cc }} />
                    <span className="text-xs font-bold" style={{ color: cc }}>{nextRaid.myCharName}</span>
                    <span className="text-[10px] text-white/40">· you</span>
                  </div>
                )}
                {nextRaid.softReserveInfo?.enabled && (
                  <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border backdrop-blur-sm text-xs font-bold ${
                    (nextRaid.softReserveInfo.my_count || 0) >= (nextRaid.softReserveInfo.max_reserves || 2)
                      ? "bg-emerald-500/20 border-emerald-500/30 text-emerald-300"
                      : "bg-amber-500/20 border-amber-500/30 text-amber-300"
                  }`}>
                    📋 SR {nextRaid.softReserveInfo.my_count || 0}/{nextRaid.softReserveInfo.max_reserves || 2}
                  </div>
                )}
              </div>
            </div>
          </button>
        );
      })()}

      {/* LATER — compact cinematic cards */}
      {restRaids.length > 0 && (
        <div>
          <h3 className="text-xs font-bold uppercase text-white/40 mb-3 tracking-wider">Later</h3>
          <div className="space-y-2.5">
            {restRaids.map((raid) => renderCompactRaidCard(raid))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {activeRaids.length === 0 && (
        <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] py-10 text-center">
          <div className="text-3xl mb-2">🗓️</div>
          <div className="font-bold text-white/70">No upcoming raids</div>
          <div className="text-xs text-white/30 mt-1">Your scheduled raids will appear here</div>
        </div>
      )}

      {/* PAST — collapsed by default, dimmed */}
      {pastRaids.length > 0 && (
        <div>
          <button
            onClick={() => setShowPast(v => !v)}
            className="flex items-center gap-2 text-xs font-bold uppercase text-white/40 tracking-wider active:text-white/70 transition-colors"
          >
            <span>{showPast ? "Hide" : "Show"} Past Raids ({pastRaids.length})</span>
            <svg className={`w-3 h-3 transition-transform duration-300 ${showPast ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showPast && (
            <div className="space-y-2.5 mt-3">
              {pastRaids.map((raid) => renderCompactRaidCard(raid, true))}
            </div>
          )}
        </div>
      )}

      {/* Announcements */}
      <div>
        <h3 className="text-xs font-bold uppercase text-white/40 mb-3 tracking-wider">Announcements</h3>
        <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4">
          <AnnouncementsFeed />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MOBILE PROFILE COMPONENT - Added characters with edit/retire, guild tags, images
// ============================================================================
function MobileProfile({ 
  me, 
  characters, 
  fieldsByGame, 
  gameNames,
  onLogout,
  onCharactersChange 
}: { 
  me: Me; 
  characters: Character[]; 
  fieldsByGame: FieldsByGame; 
  gameNames: Record<string, string>;
  onLogout: () => void;
  onCharactersChange: () => void;
}) {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [editingChar, setEditingChar] = useState<Character | null>(null);
  const [crossGuildMap, setCrossGuildMap] = useState<Record<string, boolean>>({});
  // Optimistic overlay for the guildless free-agent toggle, keyed by character id.
  const [freeAgentMap, setFreeAgentMap] = useState<Record<string, boolean>>({});
  const [editName, setEditName] = useState("");
  const [editOptions, setEditOptions] = useState<Record<number, number | number[]>>({});
  const [saving, setSaving] = useState(false);

  const openEditModal = (char: Character) => {
    setEditingChar(char);
    setEditName(char.name);
    
    // Build options map from character's existing options
    const optsMap: Record<number, number | number[]> = {};
    if (Array.isArray(char.options)) {
      for (const entry of char.options) {
        const gid = entry.group_id;
        const optId = entry.option_id;
        const groupDef = fieldsForGame(fieldsByGame, char.gameId).find(g => g.id === gid);
        if (groupDef?.multiSelect) {
          const existing = optsMap[gid] as number[] || [];
          optsMap[gid] = [...existing, optId];
        } else {
          optsMap[gid] = optId;
        }
      }
    }
    setEditOptions(optsMap);
  };

  // Fetch cross-guild opt-in status for each guild
  useEffect(() => {
    const guildIds = Array.from(new Set(characters.filter(c => c.guildId).map(c => c.guildId!)));
    guildIds.forEach(gId => {
      fetch(`/api/guilds/${gId}/members/cross-guild`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : { allowCrossGuild: false })
        .then(d => setCrossGuildMap(prev => ({ ...prev, [gId]: d.allowCrossGuild || false })))
        .catch(() => {});
    });
  }, [characters]);

  const handleSaveEdit = async () => {
    if (!editingChar || !editName.trim()) return;
    
    setSaving(true);
    try {
      const payloadOptions: Record<number, number | number[]> = {};
      for (const [gidStr, val] of Object.entries(editOptions)) {
        const gid = Number(gidStr);
        if (Array.isArray(val)) {
          if (val.length > 0) payloadOptions[gid] = val;
        } else if (val > 0) {
          payloadOptions[gid] = val;
        }
      }

      const res = await fetch(`${API_URL}/characters/${editingChar.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          gameId: editingChar.gameId,
          name: editName.trim(),
          options: payloadOptions,
          metadata: editingChar.metadata || {}
        })
      });

      if (res.ok) {
        showToast("Character updated!");
        setEditingChar(null);
        onCharactersChange();
      } else {
        const data = await res.json();
        showToast(data.message || "Failed to save", "error");
      }
    } catch {
      showToast("Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleRetire = async (char: Character) => {
    const isConfirmed = await confirm({
      title: "Retire Character?",
      description: `Are you sure you want to retire ${char.name}? This action cannot be undone.`,
      confirmText: "Retire",
      variant: "danger"
    });
    
    if (!isConfirmed) return;
    
    try {
      const res = await fetch(`${API_URL}/characters/${char.id}`, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (res.ok) {
        showToast(`${char.name} retired`);
        onCharactersChange();
      } else {
        showToast("Failed to retire character", "error");
      }
    } catch {
      showToast("Failed to retire character", "error");
    }
  };

  if (!me) {
    return (
      <div className="relative h-full flex flex-col items-center justify-center p-6 pb-28 overflow-hidden bg-[#09090b]">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-indigo-500/10 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute bottom-1/3 left-0 w-48 h-48 bg-purple-500/8 rounded-full blur-[70px] pointer-events-none" />
        <div className="relative z-10 flex flex-col items-center text-center w-full max-w-xs">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500/25 to-purple-600/10 border border-indigo-500/25 flex items-center justify-center mb-6 shadow-2xl shadow-indigo-500/10">
            <svg className="w-9 h-9 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 className="text-2xl font-black text-white mb-2 tracking-tight">Your Profile</h2>
          <p className="text-white/40 text-sm leading-relaxed mb-8">Manage your characters, specs, and guild membership all in one place.</p>
          <a href={`${API_URL}/auth/discord`} className="w-full py-4 bg-[#5865F2] hover:bg-[#4752C4] rounded-2xl font-bold text-white text-base flex items-center justify-center gap-3 shadow-lg shadow-[#5865F2]/25 transition-all hover:scale-[1.02] active:scale-[0.98]">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037 14.178 14.178 0 0 0-.624 1.29 18.423 18.423 0 0 0-4.825 0 14.12 14.12 0 0 0-.63-1.29.074.074 0 0 0-.077-.037 19.736 19.736 0 0 0-4.885 1.515.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.086 2.157 2.419 0 1.334-.956 2.42-2.157 2.42zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.086 2.157 2.419 0 1.334-.946 2.42-2.157 2.42z"/></svg>
            Continue with Discord
          </a>
          <p className="mt-4 text-[11px] text-white/25 tracking-wide">Free · No ads · Secure Discord login</p>
        </div>
      </div>
    );
  }

  // The edit modal offers the edited character's own game's classes and specs.
  const fields = fieldsForGame(fieldsByGame, editingChar?.gameId);
  // Label each character's game only when they span more than one (e.g. Anniversary + Forever).
  const spansGames = new Set(characters.map(c => String(c.gameId))).size > 1;

  // Get role options for edit modal
  const roleGroup = fields.find(g => g.key === "spec");
  const classGroup = fields.find(g => g.key === "class");
  const raidInterestGroup = fields.find(g => g.key === "raid_interest" || g.key === "raid_preferences");

  // Get selected class key for role filtering
  const selectedClassId = classGroup ? editOptions[classGroup.id] : null;
  const selectedClassOpt = classGroup?.options.find(o => o.id === selectedClassId);
  const selectedClassKey = selectedClassOpt?.key;

  // Filter roles based on selected class
  const availableRoles = roleGroup?.options.filter(role => {
    if (!role.metadata?.allowed_class_keys) return true;
    if (!selectedClassKey) return true;
    return role.metadata.allowed_class_keys.includes(selectedClassKey);
  }) || [];

  // Resolve a character's spec label from its saved options (fallback to metadata).
  const specLabelForChar = (char: Character): string | null => {
    const specGroup = fieldsForGame(fieldsByGame, char.gameId).find(g => g.key === "spec");
    if (specGroup && Array.isArray(char.options)) {
      const sel = char.options.find(o => o.group_id === specGroup.id);
      const found = sel && specGroup.options.find(so => so.id === sel.option_id);
      if (found) return found.label;
    }
    return (char.metadata as any)?.primary_spec_label || null;
  };

  return (
    <div className="pb-24 bg-[#09090b] min-h-full">
      {/* Profile Header — glow banner */}
      <div className="relative px-4 pt-10 pb-7 overflow-hidden">
        <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-80 h-64 bg-indigo-500/15 rounded-full blur-[90px] pointer-events-none" />
        <div className="absolute top-0 right-0 w-40 h-40 bg-purple-500/10 rounded-full blur-[70px] pointer-events-none" />
        <div className="relative z-10 flex flex-col items-center">
          <div className="w-24 h-24 rounded-full p-[3px] bg-gradient-to-br from-indigo-400 to-purple-600 shadow-2xl shadow-indigo-500/20 mb-4">
            {me.avatar ? (
              <img src={me.avatar} alt="Avatar" className="w-full h-full rounded-full bg-[#09090b] object-cover" />
            ) : (
              <div className="w-full h-full rounded-full bg-[#16161c] flex items-center justify-center text-3xl font-black text-indigo-300">
                {me.username.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <h2 className="text-2xl font-black text-white tracking-tight">{me.username}</h2>
          <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10">
            <svg className="w-3.5 h-3.5 text-indigo-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
            <span className="text-xs font-bold text-white/70">{characters.length} {characters.length === 1 ? "character" : "characters"}</span>
          </div>
        </div>
      </div>

      {/* Characters Section */}
      <div className="px-4 mb-6">
        <h3 className="text-xs font-bold uppercase text-white/40 mb-3 tracking-wider">Your Characters</h3>
        {characters.length === 0 ? (
          <div className="text-center text-white/40 py-4">No characters yet</div>
        ) : (
          <div className="space-y-3">
            {characters.map((char) => {
              const className = char.metadata?.class_name || "Unknown";
              const charFields = fieldsForGame(fieldsByGame, char.gameId);
              const classColor = getClassColorFromFields(className, charFields);
              const avatarImage = char.metadata?.render_url;
              const wclScore = char.metadata?.wcl_perf?.score;
              const specLabel = specLabelForChar(char);
              const roleColor = roleAccentColor(char.metadata?.primary_role);
              const specIcon = getSpecIconUrl(specLabel, className, charFields) || getClassIconUrl(className, charFields);

              return (
                <div key={char.id} className={`relative rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.05] to-white/[0.01] overflow-hidden${char.isSleeping ? " opacity-60" : ""}`}>
                  {/* role accent bar */}
                  <div className="absolute left-0 top-0 bottom-0 w-1 z-10" style={{ backgroundColor: roleColor }} />
                  {/* Character Info */}
                  <div className="p-3 pl-4 flex items-center gap-3">
                    {/* Avatar with Blizzard render or fallback */}
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center font-black text-xl overflow-hidden border-2 shrink-0"
                      style={{
                        backgroundColor: `${classColor}20`,
                        borderColor: classColor
                      }}
                    >
                      {avatarImage ? (
                        <img
                          src={avatarImage}
                          alt={char.name}
                          className="w-full h-full object-cover scale-150 translate-y-1"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.parentElement!.innerHTML = `<span style="color:${classColor}">${char.name[0]}</span>`;
                          }}
                        />
                      ) : (
                        <span style={{ color: classColor }}>{char.name[0]}</span>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      {/* Guild Tag + Name */}
                      <div className="font-black text-base truncate leading-tight">
                        {char.guildTag && (
                          <span className="text-cyan-400">[{char.guildTag}] </span>
                        )}
                        <span style={{ color: classColor }}>{char.name}</span>
                      </div>
                      {/* Spec + class + guild */}
                      <div className="flex items-center gap-1.5 mt-1 text-xs min-w-0">
                        {specIcon && (
                          <img src={specIcon} alt="" className="w-4 h-4 rounded shrink-0" style={{ border: `1px solid ${classColor}55` }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        )}
                        <span className="font-bold truncate" style={{ color: roleColor }}>{specLabel || className}</span>
                        <span className="text-white/20 shrink-0">·</span>
                        <span className="text-white/40 truncate">{className}</span>
                      </div>
                      {(char.guildName || spansGames || char.isSleeping) && (
                        <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
                          {char.isSleeping && (
                            <span className="shrink-0 rounded border border-white/15 bg-white/5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white/50">
                              Asleep
                            </span>
                          )}
                          {spansGames && <GameChip name={shortGameName(gameNames[String(char.gameId)])} />}
                          {char.guildName && <span className="text-[11px] text-white/30 truncate">{char.guildName}</span>}
                        </div>
                      )}
                    </div>

                    {/* WCL Score */}
                    {wclScore && (
                      <div
                        className="px-2 py-1 rounded-lg text-xs font-black shrink-0"
                        style={{
                          backgroundColor: `${getParseColor(wclScore)}20`,
                          color: getParseColor(wclScore)
                        }}
                      >
                        {Math.round(wclScore)}
                      </div>
                    )}
                  </div>

                  {/* Cross-Guild Toggle. Guilded characters flip their membership flag
                      (and still need the guild to release the week); guildless ones flip
                      their own free-agent flag and only need availability set. */}
                  {char.guildId ? (
                    <div className="flex items-center justify-between px-3 py-2 border-t border-white/10 bg-white/[0.02]">
                      <span className="text-[10px] text-white/40 font-medium">Open to other raid teams</span>
                      <button
                        onClick={async () => {
                          const gId = char.guildId!;
                          const newVal = !crossGuildMap[gId];
                          try {
                            const res = await fetch(`/api/guilds/${gId}/members/cross-guild`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json' },
                              credentials: 'include',
                              body: JSON.stringify({ allowCrossGuild: newVal }),
                            });
                            if (res.ok) setCrossGuildMap(prev => ({ ...prev, [gId]: newVal }));
                          } catch (err) { console.error(err); }
                        }}
                        className={`relative w-9 h-5 rounded-full transition-colors duration-200 shrink-0 ${crossGuildMap[char.guildId] ? 'bg-cyan-500' : 'bg-white/10'}`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${crossGuildMap[char.guildId] ? 'translate-x-4' : ''}`} />
                      </button>
                    </div>
                  ) : (() => {
                    const faOn = freeAgentMap[char.id] ?? (char.freeAgent !== false);
                    return (
                      <div className="flex items-center justify-between px-3 py-2 border-t border-white/10 bg-white/[0.02]">
                        <span className="text-[10px] text-white/40 font-medium">
                          Open to other raid teams
                          <span className="block text-[9px] text-white/25">Free agent · confirm each week to be listed</span>
                        </span>
                        <button
                          onClick={async () => {
                            const newVal = !faOn;
                            try {
                              const res = await fetch(`/characters/${char.id}/free-agent`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                credentials: 'include',
                                body: JSON.stringify({ freeAgent: newVal }),
                              });
                              if (res.ok) setFreeAgentMap(prev => ({ ...prev, [char.id]: newVal }));
                            } catch (err) { console.error(err); }
                          }}
                          className={`relative w-9 h-5 rounded-full transition-colors duration-200 shrink-0 ${faOn ? 'bg-teal-500' : 'bg-white/10'}`}
                        >
                          <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${faOn ? 'translate-x-4' : ''}`} />
                        </button>
                      </div>
                    );
                  })()}

                  {/* Action Buttons */}
                  <div className="flex border-t border-white/10">
                    <button
                      onClick={() => openEditModal(char)}
                      className="flex-1 py-2.5 flex items-center justify-center gap-2 text-white/50 hover:text-white hover:bg-white/5 transition-colors text-xs font-bold uppercase"
                    >
                      <Icons.Edit />
                      Edit
                    </button>
                    <div className="w-px bg-white/10" />
                    <button
                      onClick={() => handleRetire(char)}
                      className="flex-1 py-2.5 flex items-center justify-center gap-2 text-white/50 hover:text-red-400 hover:bg-red-500/10 transition-colors text-xs font-bold uppercase"
                    >
                      <Icons.Trash />
                      Retire
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Logout Button */}
      <div className="px-4 space-y-3">
        <div className="text-xs text-white/30 text-center mb-2">
          For full roster/guild management, use desktop
        </div>
        <button onClick={onLogout} className="w-full px-4 py-3 bg-red-500/15 hover:bg-red-500/25 text-red-400 rounded-xl font-bold border border-red-500/20 transition-colors">
          Log Out
        </button>
      </div>

      {/* Edit Modal */}
      {editingChar && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center">
          <div className="bg-[#121214] w-full max-w-md rounded-t-2xl sm:rounded-2xl border border-white/10 max-h-[90vh] overflow-hidden flex flex-col">
            {/* Grabber handle (mobile bottom-sheet) */}
            <div className="sm:hidden flex justify-center pt-2.5 pb-1">
              <span className="w-10 h-1 rounded-full bg-white/20" />
            </div>
            {/* Modal Header */}
            <div className="px-4 pb-4 pt-2 sm:pt-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <h3 className="font-black text-lg">Edit Character</h3>
                {spansGames && <GameChip name={shortGameName(gameNames[String(editingChar.gameId)])} />}
              </div>
              <button 
                onClick={() => setEditingChar(null)}
                className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center"
              >
                ✕
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-4 overflow-y-auto flex-1 space-y-4">
              {/* Name */}
              <div>
                <label className="text-xs text-white/50 block mb-1">Character Name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  maxLength={24}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-cyan-500"
                />
              </div>
              
              {/* Role Selection */}
              {roleGroup && (
                <div>
                  <label className="text-xs text-white/50 block mb-2">Spec</label>
                  <div className="flex flex-wrap gap-2">
                    {availableRoles.map((role) => {
                      const isSelected = roleGroup.multiSelect
                        ? (editOptions[roleGroup.id] as number[] || []).includes(role.id)
                        : editOptions[roleGroup.id] === role.id;
                      
                      return (
                        <button
                          key={role.id}
                          onClick={() => {
                            if (roleGroup.multiSelect) {
                              const current = (editOptions[roleGroup.id] as number[]) || [];
                              const newVal = isSelected 
                                ? current.filter(id => id !== role.id)
                                : [...current, role.id];
                              setEditOptions({ ...editOptions, [roleGroup.id]: newVal });
                            } else {
                              setEditOptions({ ...editOptions, [roleGroup.id]: role.id });
                            }
                          }}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            isSelected
                              ? "bg-cyan-600 text-white"
                              : "bg-white/5 text-white/60 border border-white/10"
                          }`}
                        >
                          {role.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Raid Interest — which raids this character wants to run */}
              {raidInterestGroup && raidInterestGroup.options.length > 0 && (
                <div className="pt-4 border-t border-white/10">
                  <label className="text-xs text-white/50 block mb-1 flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    Interested Raids
                  </label>
                  <p className="text-[10px] text-white/30 mb-2.5">Tap the raids you want to be considered for.</p>
                  <div className="flex flex-wrap gap-2">
                    {raidInterestGroup.options.map((opt) => {
                      const current = (editOptions[raidInterestGroup.id] as number[]) || [];
                      const isSelected = current.includes(opt.id);
                      return (
                        <button
                          key={opt.id}
                          onClick={() => {
                            const next = isSelected ? current.filter(id => id !== opt.id) : [...current, opt.id];
                            setEditOptions({ ...editOptions, [raidInterestGroup.id]: next });
                          }}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${
                            isSelected
                              ? "bg-emerald-500/20 border-emerald-500 text-emerald-200"
                              : "bg-black/40 border-white/10 text-white/50 hover:border-white/20"
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-white/10 flex gap-3" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 16px)' }}>
              <button
                onClick={() => setEditingChar(null)}
                className="flex-1 py-3 bg-white/10 rounded-xl font-bold text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving || !editName.trim()}
                className="flex-1 py-3 bg-cyan-600 rounded-xl font-bold text-white disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MOBILE OFFICER — the two guild-leader jobs that are genuinely time-sensitive
// and least likely to happen at a desk: deciding join requests, and flipping
// the weekly release. Everything else in guild management (roster, squads,
// loot, ARC) stays on desktop deliberately; this tab is the phone subset.
//
// Only rendered when the user owns or is an officer of at least one guild.
// Every endpoint below is already officer-gated server side, so this is a view
// over existing permissions, not a new one.
// ============================================================================

/** Compact "3 days ago" without pulling luxon into the mobile bundle. */
function relativeSince(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

/**
 * "Aug 19 - Aug 25" for a reset week. `weekStart` is the shared Wednesday-UTC
 * label, so it goes through the guild's rule: a US week starts on Tuesday.
 */
function formatReleaseWeek(weekStart: string, rule: ResetRule = DEFAULT_RESET_RULE): string {
  return formatWeekRange(weekStart, rule);
}

// ============================================================================
// MOBILE ROSTER — "who is in my guild", sized for a phone.
//
// A guild is 30 to 80 people with one to three characters each, so a flat
// character list is a wall. One row per PLAYER instead: their name, their
// characters as class-coloured icons, and the one state an officer acts on
// from a phone (did they confirm next week). A row expands into its
// characters, each with spec, class and, when the guild plays more than one
// game, which game. Filters cover the questions officers actually ask: who has
// not confirmed, who is an officer, who is asleep, who plays which game.
// ============================================================================

type RosterAvailability = "confirmed" | "set" | "stale" | "missing";

/** One character from GET /api/guilds/:id/mobile-roster. */
interface MobileRosterChar {
  id: string;
  name: string;
  gameId: string;
  gameName: string | null;
  userId: string | null;
  displayName: string | null;
  discordId: string | null;
  ilvl: number | null;
  isSleeping: boolean;
  className: string | null;
  specLabel: string | null;
  role: string | null;
  isOfficer: boolean;
  joinedAt: string;
  availabilityStatus: RosterAvailability;
  upcomingRaids: number;
}

interface MobileRosterPayload {
  games: { gameId: string; gameName: string; isActive: boolean }[];
  characters: MobileRosterChar[];
  weekInfo?: { currentWeek: string; nextWeek: string; resetRule?: ResetRule };
}

interface RosterPlayer {
  key: string;
  name: string;
  isOfficer: boolean;
  availability: RosterAvailability;
  characters: MobileRosterChar[];
}

type RosterStatusFilter = "all" | "unconfirmed" | "officers" | "asleep";

const AVAIL_LABEL: Record<RosterAvailability, string> = {
  confirmed: "Confirmed",
  set: "Set, not confirmed",
  stale: "Last week only",
  missing: "No availability",
};
const AVAIL_COLOR: Record<RosterAvailability, string> = {
  confirmed: "#34d399",
  set: "#fbbf24",
  stale: "#94a3b8",
  missing: "#fb7185",
};

/** Characters grouped by owner. Officers first, then by name, as the server sends them. */
function groupRosterPlayers(chars: MobileRosterChar[]): RosterPlayer[] {
  const byKey = new Map<string, RosterPlayer>();
  for (const c of chars) {
    const key = c.userId || c.id;
    let p = byKey.get(key);
    if (!p) {
      p = { key, name: c.displayName || c.name, isOfficer: false, availability: c.availabilityStatus, characters: [] };
      byKey.set(key, p);
    }
    p.isOfficer = p.isOfficer || c.isOfficer;
    p.characters.push(c);
  }
  // Awake characters first within a player; asleep ones trail.
  const players = Array.from(byKey.values());
  players.forEach(p => p.characters.sort((a, b) => Number(a.isSleeping) - Number(b.isSleeping)));
  return players;
}

function MobileGuildRoster({
  guild,
  data,
  fieldsByGame,
  initialStatus = "all",
  onClose,
}: {
  guild: OfficerGuild | null;
  data: MobileRosterPayload | null;
  fieldsByGame: FieldsByGame;
  initialStatus?: RosterStatusFilter;
  onClose: () => void;
}) {
  const [search, setSearch] = useState("");
  const [gameFilter, setGameFilter] = useState<string>("");
  const [status, setStatus] = useState<RosterStatusFilter>(initialStatus);
  const [open, setOpen] = useState<Set<string>>(new Set());

  const allChars = data?.characters || [];
  // Game chips only when the roster spans more than one game.
  const gameIds = Array.from(new Set(allChars.map(c => c.gameId)));
  const multiGame = gameIds.length > 1;
  const gameNameFor = (gid: string) =>
    shortGameName(data?.games.find(g => g.gameId === gid)?.gameName || allChars.find(c => c.gameId === gid)?.gameName) || "Game";

  const q = search.trim().toLowerCase();
  const players = groupRosterPlayers(allChars)
    .map(p => ({ ...p, characters: gameFilter ? p.characters.filter(c => c.gameId === gameFilter) : p.characters }))
    .filter(p => p.characters.length > 0)
    .filter(p => {
      if (status === "unconfirmed" && p.availability === "confirmed") return false;
      if (status === "officers" && !p.isOfficer) return false;
      if (status === "asleep" && !p.characters.some(c => c.isSleeping)) return false;
      if (q && !p.name.toLowerCase().includes(q) && !p.characters.some(c => c.name.toLowerCase().includes(q))) return false;
      return true;
    });

  const toggle = (key: string) =>
    setOpen(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });

  const charIcon = (c: MobileRosterChar, size: string) => {
    const fields = fieldsForGame(fieldsByGame, c.gameId);
    const cls = c.className || "Unknown";
    const color = getClassColorFromFields(cls, fields);
    const url = getSpecIconUrl(c.specLabel, cls, fields) || getClassIconUrl(cls, fields);
    return (
      <div
        key={c.id}
        className={`${size} rounded-md overflow-hidden shrink-0 flex items-center justify-center text-[10px] font-black ${c.isSleeping ? "opacity-40" : ""}`}
        style={{ border: `1px solid ${color}66`, backgroundColor: `${color}1f`, color }}
        title={c.name}
      >
        {url ? (
          <img src={url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          cls.charAt(0)
        )}
      </div>
    );
  };

  const chip = (active: boolean, label: React.ReactNode, onClick: () => void, key: string) => (
    <button
      key={key}
      onClick={onClick}
      className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold border transition-colors ${
        active ? "bg-cyan-500/15 border-cyan-400/40 text-cyan-200" : "bg-white/[0.03] border-white/10 text-white/45"
      }`}
    >
      {label}
    </button>
  );

  const totalPlayers = groupRosterPlayers(allChars).length;
  const unconfirmed = groupRosterPlayers(allChars).filter(p => p.availability !== "confirmed").length;

  return (
    <div className="fixed inset-0 z-50 bg-[#09090b] text-white flex flex-col">
      {/* Header */}
      <div className="shrink-0 px-4 pt-4 pb-3 border-b border-white/10 bg-[#0E1015]">
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="-ml-2 p-2 rounded-xl text-white/70 active:bg-white/10" aria-label="Back">
            <Icons.Back />
          </button>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-400/70">Roster</div>
            <div className="text-base font-black truncate leading-tight">{guild?.name || "Your guild"}</div>
          </div>
          <div className="text-right text-[11px] text-white/40 leading-tight">
            <div><span className="font-bold text-white/80">{totalPlayers}</span> players</div>
            <div><span className="font-bold text-white/80">{allChars.length}</span> characters</div>
          </div>
        </div>

        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search players or characters"
          className="mt-3 w-full rounded-xl bg-white/[0.04] border border-white/10 px-3.5 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-cyan-400/50"
        />

        <div className="mt-2.5 -mx-4 px-4 overflow-x-auto">
          <div className="flex gap-2 w-max">
            {chip(status === "all", "Everyone", () => setStatus("all"), "all")}
            {chip(status === "unconfirmed", <>Not confirmed{unconfirmed > 0 && <span className="ml-1 text-amber-300">{unconfirmed}</span>}</>, () => setStatus("unconfirmed"), "unconfirmed")}
            {chip(status === "officers", "Officers", () => setStatus("officers"), "officers")}
            {chip(status === "asleep", "Asleep", () => setStatus("asleep"), "asleep")}
            {multiGame && <span className="w-px bg-white/10 my-1" />}
            {multiGame && chip(gameFilter === "", "All games", () => setGameFilter(""), "g-all")}
            {multiGame && gameIds.map(gid =>
              chip(gameFilter === gid, <>{gameNameFor(gid)} <span className="text-white/40">{allChars.filter(c => c.gameId === gid).length}</span></>, () => setGameFilter(gid), `g-${gid}`)
            )}
          </div>
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-3 space-y-2" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)" }}>
        {!data ? (
          <div className="py-12 text-center text-white/30 text-sm">Loading...</div>
        ) : players.length === 0 ? (
          <div className="py-12 text-center text-white/30 text-sm">Nobody matches.</div>
        ) : players.map(p => {
          const isOpen = open.has(p.key);
          const color = AVAIL_COLOR[p.availability];
          // "2 Anniversary · 1 Forever" only when the guild plays more than one game.
          const gameSummary = multiGame
            ? gameIds
                .map(gid => [gid, p.characters.filter(c => c.gameId === gid).length] as const)
                .filter(([, n]) => n > 0)
                .map(([gid, n]) => `${n} ${gameNameFor(gid)}`)
                .join(" · ")
            : null;
          return (
            <div key={p.key} className="relative rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: color }} />
              <button onClick={() => toggle(p.key)} className="w-full text-left pl-4 pr-3 py-3 flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-black truncate">{p.name}</span>
                    {p.isOfficer && (
                      <span className="shrink-0 rounded border border-cyan-400/30 bg-cyan-400/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-cyan-300">Officer</span>
                    )}
                  </div>
                  <div className="mt-1.5 flex items-center gap-1.5">
                    {p.characters.slice(0, 6).map(c => charIcon(c, "w-6 h-6"))}
                    {p.characters.length > 6 && <span className="text-[11px] text-white/40">+{p.characters.length - 6}</span>}
                    {gameSummary && <span className="ml-1 text-[11px] text-white/40 truncate">{gameSummary}</span>}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color }}>
                    {p.availability === "confirmed" ? "Confirmed" : p.availability === "set" ? "Not confirmed" : p.availability === "stale" ? "Stale" : "Missing"}
                  </div>
                  <div className="text-[10px] text-white/30 mt-0.5">next week</div>
                </div>
                <svg className={`w-4 h-4 text-white/30 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isOpen && (
                <div className="border-t border-white/5 pl-4 pr-3 py-2 space-y-1.5">
                  <div className="text-[11px] text-white/40 pb-1">{AVAIL_LABEL[p.availability]} for next week</div>
                  {p.characters.map(c => {
                    const fields = fieldsForGame(fieldsByGame, c.gameId);
                    const cls = c.className || "Unknown";
                    const ccolor = getClassColorFromFields(cls, fields);
                    return (
                      <div key={c.id} className={`flex items-center gap-3 py-1.5 ${c.isSleeping ? "opacity-60" : ""}`}>
                        {charIcon(c, "w-9 h-9")}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="font-bold truncate" style={{ color: ccolor }}>{c.name}</span>
                            {c.isSleeping && (
                              <span className="shrink-0 rounded border border-white/15 bg-white/5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white/50">Asleep</span>
                            )}
                            {multiGame && <GameChip name={gameNameFor(c.gameId)} />}
                          </div>
                          <div className="text-[11px] text-white/45 truncate">
                            <span style={{ color: roleAccentColor(c.role) }}>{c.specLabel || shortRole(c.role) || "No spec"}</span>
                            {" · "}{cls}
                            {c.ilvl ? ` · ilvl ${c.ilvl}` : ""}
                            {c.upcomingRaids > 0 ? ` · in ${c.upcomingRaids} ${c.upcomingRaids === 1 ? "raid" : "raids"}` : ""}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MobileOfficer({
  guilds,
  fieldsByGame,
  pendingCounts,
  onRequestsChanged,
}: {
  guilds: OfficerGuild[];
  fieldsByGame: FieldsByGame;
  pendingCounts: Record<string, number>;
  onRequestsChanged: () => void;
}) {
  const { showToast } = useToast();
  const [guildId, setGuildId] = useState<string>(guilds[0]?.id || "");
  const [readiness, setReadiness] = useState<GuildReadiness | null>(null);
  const [roster, setRoster] = useState<MobileRosterPayload | null>(null);
  const [rosterView, setRosterView] = useState<RosterStatusFilter | null>(null);
  const [requests, setRequests] = useState<JoinRequestRow[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [releaseBusy, setReleaseBusy] = useState<string | null>(null);
  const [showReleaseInfo, setShowReleaseInfo] = useState(false);
  const [declining, setDeclining] = useState<{ req: JoinRequestRow; note: string; reasonKey: string | null } | null>(null);
  const [reasons, setReasons] = useState<{ key: string; label: string }[]>([]);
  const NOTE_MAX = 300;

  const guild = guilds.find(g => g.id === guildId) || guilds[0] || null;

  // Keep the selection valid if the officer's guild list changes underneath us.
  useEffect(() => {
    if (guilds.length > 0 && !guilds.some(g => g.id === guildId)) {
      setGuildId(guilds[0].id);
    }
  }, [guilds, guildId]);

  // Decline presets are owned by the server so the applicant-facing wording
  // stays identical to desktop.
  useEffect(() => {
    let alive = true;
    fetch("/join-decline-reasons", { credentials: "include" })
      .then(r => (r.ok ? r.json() : { reasons: [] }))
      .then(d => { if (alive) setReasons(Array.isArray(d.reasons) ? d.reasons : []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const loadGuild = React.useCallback(() => {
    if (!guildId) return;
    setReadiness(null);
    setRequests(null);
    fetch(`/api/guilds/${guildId}/readiness`, { credentials: "include" })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) setReadiness(d); })
      .catch(console.error);
    fetch(`/guilds/${guildId}/join-requests`, { credentials: "include" })
      .then(r => (r.ok ? r.json() : []))
      .then(d => setRequests(Array.isArray(d) ? d : []))
      .catch(() => setRequests([]));
    setRoster(null);
    fetch(`/api/guilds/${guildId}/mobile-roster`, { credentials: "include" })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d && Array.isArray(d.characters)) setRoster(d); })
      .catch(() => {});
  }, [guildId]);

  useEffect(() => { loadGuild(); }, [loadGuild]);

  const toggleRelease = async (weekStart: string, currentlyReleased: boolean) => {
    if (!readiness) return;
    const isNext = weekStart === readiness.nextWeek;
    const next = !currentlyReleased;

    // Optimistic flip so the toggle responds instantly on a phone connection.
    setReadiness(prev => prev && ({
      ...prev,
      ...(isNext ? { releasedNextWeek: next } : { releasedCurrentWeek: next }),
    }));
    setReleaseBusy(weekStart);

    try {
      const res = await fetch(`/api/guilds/${guildId}/release`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ weekStart, released: next }),
      });
      if (!res.ok) {
        setReadiness(prev => prev && ({
          ...prev,
          ...(isNext ? { releasedNextWeek: !next } : { releasedCurrentWeek: !next }),
        }));
        showToast("Could not change release. Try again.", "error");
        return;
      }
      // Desktop surfaces listen for this to recount without a reload.
      window.dispatchEvent(new CustomEvent("lfm:guild-release-changed", {
        detail: { guildId, weekStart, released: next },
      }));
      showToast(next ? "Week released to the community." : "Release revoked.", "success");
    } catch {
      setReadiness(prev => prev && ({
        ...prev,
        ...(isNext ? { releasedNextWeek: !next } : { releasedCurrentWeek: !next }),
      }));
      showToast("Network error. Try again.", "error");
    } finally {
      setReleaseBusy(null);
    }
  };

  const decide = async (
    req: JoinRequestRow,
    action: "approve" | "reject",
    payload?: { reasonKey?: string | null; note?: string },
  ) => {
    setBusyId(req.id);
    try {
      const res = await fetch(`/guilds/join-requests/${req.id}/${action}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(action === "reject" ? (payload || {}) : {}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Same error vocabulary as the desktop Requests tab, so an officer who
        // hits one of these gets the same explanation on either device.
        const msg =
          data.error === "already_in_guild" ? "That character already joined a guild with an invite code, so the application was closed." :
          data.error === "character_gone" ? "That character no longer exists, so the application was closed." :
          data.error === "realm_mismatch" ? (data.message || "Realm mismatch.") :
          data.error === "faction_mismatch" ? (data.message || "Faction mismatch.") :
          data.error === "not_pending" ? "This application was already decided." :
          data.error === "note_too_long" ? `Your note is too long (max ${data.max} characters).` :
          data.error === "invalid_reason" ? "Pick one of the listed reasons." :
          "Something went wrong. Try again.";
        showToast(msg, "error");
      } else {
        setDeclining(null);
        showToast(
          action === "approve"
            ? `${req.characterName || "Applicant"} joined the guild.`
            : "Application declined.",
          "success",
        );
      }
      // Refresh either way: most failures mean the request is no longer pending.
      loadGuild();
      onRequestsChanged();
    } catch {
      showToast("Something went wrong. Try again.", "error");
      loadGuild();
      onRequestsChanged();
    } finally {
      setBusyId(null);
    }
  };

  if (guilds.length === 0) return null;

  const pending = requests || [];

  if (rosterView) {
    return (
      <MobileGuildRoster
        guild={guild}
        data={roster}
        fieldsByGame={fieldsByGame}
        initialStatus={rosterView}
        onClose={() => setRosterView(null)}
      />
    );
  }

  // Summary for the roster card. Counts are per player, since availability is.
  const rosterPlayers = roster ? groupRosterPlayers(roster.characters) : [];
  const rosterUnconfirmed = rosterPlayers.filter(p => p.availability !== "confirmed").length;
  const rosterGameIds = roster ? Array.from(new Set(roster.characters.map(c => c.gameId))) : [];
  const rosterGameName = (gid: string) =>
    shortGameName(roster?.games.find(g => g.gameId === gid)?.gameName || roster?.characters.find(c => c.gameId === gid)?.gameName) || "Game";

  return (
    <div className="pb-24 min-h-full bg-[#09090b] text-white">
      {/* Header */}
      <div className="px-4 pt-6 pb-3">
        <div className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-400/70">Officer</div>
        <h1 className="text-2xl font-black leading-tight mt-0.5">
          {guild?.name || "Your guild"}
        </h1>
        <div className="text-xs text-white/35 mt-0.5">
          {guild?.isOwner ? "Guild leader" : "Officer"}
        </div>
      </div>

      {/* Guild switcher, only when it can actually switch */}
      {guilds.length > 1 && (
        <div className="px-4 pb-3 -mx-1 overflow-x-auto">
          <div className="flex gap-2 px-1">
            {guilds.map(g => {
              const active = g.id === guildId;
              const count = pendingCounts[g.id] || 0;
              return (
                <button
                  key={g.id}
                  onClick={() => setGuildId(g.id)}
                  className={`relative shrink-0 px-3.5 py-2 rounded-xl text-xs font-bold border transition-colors ${
                    active
                      ? "bg-cyan-500/15 border-cyan-400/40 text-cyan-200"
                      : "bg-white/[0.03] border-white/10 text-white/45"
                  }`}
                >
                  {g.tag ? `[${g.tag}] ` : ""}{g.name}
                  {count > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-[9px] font-black text-black">
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── This week at a glance ─────────────────────────────── */}
      <div className="px-4 pb-4">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
          <div className="px-4 py-2.5 border-b border-white/5 bg-gradient-to-r from-cyan-500/10 to-transparent">
            <span className="text-xs font-bold uppercase tracking-wider text-white/70">Raid ready</span>
          </div>

          {!readiness ? (
            <div className="p-6 text-center text-white/30 text-sm">Loading...</div>
          ) : (
            <div className="p-3 grid grid-cols-2 gap-2">
              <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
                <div className="text-2xl font-black tabular-nums leading-none">
                  <span className={readiness.availabilityConfirmed >= readiness.players ? "text-emerald-400" : "text-cyan-300"}>
                    {readiness.availabilityConfirmed}
                  </span>
                  <span className="text-white/25 text-base">/{readiness.players}</span>
                </div>
                <div className="text-[10px] uppercase tracking-wider text-white/35 font-bold mt-1.5">Confirmed next week</div>
              </div>

              <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
                <div className="text-2xl font-black tabular-nums leading-none text-white/85">
                  {readiness.upcomingRaids}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-white/35 font-bold mt-1.5">Raids scheduled</div>
              </div>

              <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
                <div className="text-2xl font-black tabular-nums leading-none text-white/85">
                  {readiness.availabilitySet}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-white/35 font-bold mt-1.5">Availability in</div>
              </div>

              <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
                <div className={`text-sm font-black leading-none mt-1 ${readiness.discordConnected ? "text-emerald-400" : "text-amber-400"}`}>
                  {readiness.discordConnected ? "Connected" : "Not set up"}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-white/35 font-bold mt-2">Discord bot</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Roster ────────────────────────────────────────────── */}
      <div className="px-4 pb-4">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
          <button
            onClick={() => setRosterView("all")}
            className="w-full flex items-center justify-between px-4 py-2.5 border-b border-white/5 bg-gradient-to-r from-cyan-500/10 to-transparent text-left"
          >
            <span className="text-xs font-bold uppercase tracking-wider text-white/70">Roster</span>
            <span className="text-[11px] font-bold text-cyan-300">Open</span>
          </button>
          {!roster ? (
            <div className="p-6 text-center text-white/30 text-sm">Loading...</div>
          ) : (
            <div className="p-3 space-y-2">
              <button onClick={() => setRosterView("all")} className="w-full text-left rounded-xl bg-white/[0.03] border border-white/5 p-3">
                <div className="text-2xl font-black tabular-nums leading-none">
                  {rosterPlayers.length}
                  <span className="text-white/25 text-base"> players</span>
                  <span className="text-white/25 text-base"> · </span>
                  {roster.characters.length}
                  <span className="text-white/25 text-base"> characters</span>
                </div>
                {rosterGameIds.length > 1 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {rosterGameIds.map(gid => (
                      <span key={gid} className="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[11px] text-white/60">
                        <span className="font-bold text-white/85">{roster.characters.filter(c => c.gameId === gid).length}</span> {rosterGameName(gid)}
                      </span>
                    ))}
                  </div>
                )}
              </button>
              <button
                onClick={() => setRosterView(rosterUnconfirmed > 0 ? "unconfirmed" : "all")}
                className={`w-full text-left rounded-xl border p-3 flex items-center justify-between ${
                  rosterUnconfirmed > 0 ? "bg-amber-500/[0.06] border-amber-400/20" : "bg-emerald-500/[0.06] border-emerald-400/20"
                }`}
              >
                <span className={`text-sm font-bold ${rosterUnconfirmed > 0 ? "text-amber-300" : "text-emerald-300"}`}>
                  {rosterUnconfirmed > 0
                    ? `${rosterUnconfirmed} ${rosterUnconfirmed === 1 ? "player has" : "players have"} not confirmed next week`
                    : "Everyone has confirmed next week"}
                </span>
                <span className="text-[11px] font-bold text-white/40">{rosterUnconfirmed > 0 ? "See who" : ""}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Weekly release ────────────────────────────────────── */}
      <div className="px-4 pb-4">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/5 bg-gradient-to-r from-cyan-500/10 to-transparent">
            <span className="text-xs font-bold uppercase tracking-wider text-white/70">Release</span>
            <button
              onClick={() => setShowReleaseInfo(v => !v)}
              aria-label="What does release do?"
              aria-expanded={showReleaseInfo}
              className={`grid h-5 w-5 place-items-center rounded-full border text-[10px] font-bold transition-colors ${
                showReleaseInfo
                  ? "border-cyan-400/60 bg-cyan-500/20 text-cyan-200"
                  : "border-white/15 bg-white/5 text-white/40"
              }`}
            >
              ?
            </button>
          </div>

          {showReleaseInfo && (
            <div className="px-4 py-3 border-b border-white/5 bg-cyan-500/[0.04]">
              <p className="text-[12px] leading-relaxed text-white/65">
                Flipping a week on lends your raiders to the rest of the community for that week. Players who
                opted into cross-guild raiding become visible as candidates in other guilds' raids, so your
                bench still gets to play.
              </p>
              <p className="mt-2 text-[12px] leading-relaxed text-white/65">
                Nobody is lent out unless they opted in themselves, and it only ever covers the single week you
                toggle.
              </p>
            </div>
          )}

          {!readiness ? (
            <div className="p-6 text-center text-white/30 text-sm">Loading...</div>
          ) : (
            <div className="divide-y divide-white/5">
              {[
                { week: readiness.currentWeek, label: "This week", on: readiness.releasedCurrentWeek },
                { week: readiness.nextWeek, label: "Next week", on: readiness.releasedNextWeek },
              ].map(({ week, label, on }) => (
                <button
                  key={week}
                  onClick={() => toggleRelease(week, on)}
                  disabled={releaseBusy === week}
                  role="switch"
                  aria-checked={on}
                  aria-label={`${on ? "Revoke" : "Activate"} release for ${label}`}
                  className="w-full flex items-center justify-between px-4 py-3.5 active:bg-white/[0.04] transition-colors disabled:opacity-50"
                >
                  <span className="text-left">
                    <span className={`block text-sm font-bold ${on ? "text-cyan-200" : "text-white/60"}`}>{label}</span>
                    <span className="block text-[11px] text-white/30 mt-0.5">{formatReleaseWeek(week, readiness.resetRule)}</span>
                  </span>
                  <span className={`relative inline-block w-11 h-6 rounded-full shrink-0 transition-colors ${
                    on ? "bg-cyan-500" : "bg-white/10 ring-1 ring-inset ring-white/15"
                  }`}>
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-md transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Pending join requests ─────────────────────────────── */}
      <div className="px-4">
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="text-xs font-bold uppercase tracking-wider text-white/70">
            Requests
            {pending.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-[10px] font-black text-black align-middle">
                {pending.length}
              </span>
            )}
          </h2>
          <button onClick={loadGuild} className="text-[11px] font-bold text-cyan-400/70 active:text-cyan-300">
            Refresh
          </button>
        </div>

        {requests === null ? (
          <div className="py-10 text-center text-white/30 text-sm">Loading...</div>
        ) : pending.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-8 text-center">
            <div className="text-2xl mb-2 opacity-60">📭</div>
            <div className="text-sm text-white/40">No pending applications.</div>
            <div className="text-[11px] text-white/20 mt-2 leading-relaxed">
              Players in your Discord can apply with <span className="text-white/40 font-mono">/ralli join</span>,
              or from your listing in the guild directory.
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {pending.map(req => {
              const className = req.className || "Unknown";
              const fields = fieldsForGame(fieldsByGame, req.gameId);
              const color = getClassColorFromFields(className, fields);
              const iconUrl = getSpecIconUrl(req.primarySpecLabel, className, fields) || getClassIconUrl(className, fields);
              const busy = busyId === req.id;
              return (
                <div key={req.id} className="rounded-2xl border border-white/[0.07] bg-white/[0.03] overflow-hidden">
                  <div className="flex items-center gap-3 p-3">
                    <div
                      className="relative w-11 h-11 rounded-xl overflow-hidden shrink-0"
                      style={{ border: `1px solid ${color}55`, backgroundColor: `${color}1a` }}
                    >
                      {iconUrl ? (
                        <img
                          src={iconUrl}
                          alt=""
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const el = e.currentTarget;
                            el.style.display = "none";
                            const fb = el.nextElementSibling as HTMLElement | null;
                            if (fb) fb.style.display = "flex";
                          }}
                        />
                      ) : null}
                      <span
                        className="absolute inset-0 items-center justify-center text-base font-black"
                        style={{ display: iconUrl ? "none" : "flex", color }}
                      >
                        {(req.characterName || "?").charAt(0).toUpperCase()}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-bold truncate" style={{ color }}>
                          {req.characterName || "(deleted)"}
                        </span>
                        <span className={`shrink-0 px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider border ${
                          req.source === "web"
                            ? "bg-cyan-500/10 border-cyan-400/25 text-cyan-300"
                            : "bg-[#5865F2]/10 border-[#5865F2]/30 text-[#98a0f5]"
                        }`}>
                          {req.source === "web" ? "Directory" : "Discord"}
                        </span>
                      </div>
                      <div className="text-[11px] text-white/40 truncate">
                        {req.primarySpecLabel || className}
                        {req.roleLabels.length > 0 && <span className="text-white/25"> · {req.roleLabels.join(", ")}</span>}
                      </div>
                      <div className="text-[10px] text-white/25 truncate mt-0.5">
                        {req.displayName || "Unknown player"} · {relativeSince(req.requestedAt)}
                      </div>
                    </div>

                    {req.wclScore != null && req.wclScore > 0 && (
                      <div className="flex flex-col items-center shrink-0">
                        <span className="text-lg font-black tabular-nums leading-none" style={{ color: getParseColor(req.wclScore) }}>
                          {req.wclScore}
                        </span>
                        <span className="text-[8px] uppercase tracking-wider text-white/30">parse</span>
                      </div>
                    )}
                  </div>

                  {req.currentGuildName && (
                    <div className="px-3 pb-2 -mt-1">
                      <div className="text-[11px] text-amber-300/80 leading-snug">
                        Currently in <span className="font-semibold">{req.currentGuildName}</span>. Accepting moves them here.
                      </div>
                    </div>
                  )}

                  <div className="flex border-t border-white/[0.07]">
                    <button
                      onClick={() => decide(req, "approve")}
                      disabled={busy}
                      className="flex-1 py-3 text-emerald-300 bg-emerald-500/10 active:bg-emerald-500/20 text-xs font-black uppercase tracking-wider transition-colors disabled:opacity-40"
                    >
                      {busy ? "Working..." : "Accept"}
                    </button>
                    <div className="w-px bg-white/[0.07]" />
                    <button
                      onClick={() => setDeclining({ req, note: "", reasonKey: null })}
                      disabled={busy}
                      className="flex-1 py-3 text-red-300 bg-red-500/[0.06] active:bg-red-500/15 text-xs font-black uppercase tracking-wider transition-colors disabled:opacity-40"
                    >
                      Decline
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="text-[11px] text-white/25 text-center mt-6 leading-relaxed">
          Roster, squads, loot and ARC live on desktop.
        </div>
      </div>

      {/* ── Decline sheet ─────────────────────────────────────── */}
      {declining && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end" onClick={() => setDeclining(null)}>
          <div
            className="bg-[#121214] w-full rounded-t-2xl border-t border-white/10 max-h-[85vh] overflow-y-auto"
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center pt-2.5 pb-1">
              <span className="w-10 h-1 rounded-full bg-white/20" />
            </div>

            <div className="px-4 pb-4 space-y-4">
              <div>
                <div className="text-base font-black text-white">
                  Decline {declining.req.characterName || "this application"}?
                </div>
                <div className="text-[11px] text-white/35 mt-1 leading-relaxed">
                  {declining.req.characterName || "The character"} stays on{" "}
                  {declining.req.displayName || "the player"}'s roster, they just won't join the guild.
                </div>
              </div>

              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-white/35 mb-2">
                  Reason sent to them
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {reasons.map(r => {
                    const sel = declining.reasonKey === r.key;
                    return (
                      <button
                        key={r.key}
                        onClick={() => setDeclining({ ...declining, reasonKey: sel ? null : r.key })}
                        className={`px-3 py-2 rounded-lg text-[11px] font-semibold border transition-colors ${
                          sel
                            ? "bg-red-500/20 border-red-400/45 text-red-200"
                            : "bg-white/[0.03] border-white/10 text-white/45"
                        }`}
                      >
                        {r.label}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-2 text-[10px] text-white/25 leading-relaxed">
                  Pick one, or none to decline without giving a reason. Rallify sends the wording.
                </div>
              </div>

              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-white/35 mb-1.5">
                  Private note <span className="text-white/20 normal-case font-normal">· your records only</span>
                </div>
                <textarea
                  value={declining.note}
                  onChange={(e) => setDeclining({ ...declining, note: e.target.value })}
                  rows={3}
                  maxLength={NOTE_MAX}
                  placeholder="Optional, e.g. applied twice this month"
                  className="w-full px-3 py-2.5 rounded-xl bg-black/30 border border-white/10 focus:border-white/25 focus:outline-none text-sm text-white/85 placeholder-white/25 resize-none transition-colors"
                />
                <div className="text-[10px] text-white/25 text-right mt-1">{declining.note.length}/{NOTE_MAX}</div>
              </div>

              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => setDeclining(null)}
                  className="flex-1 py-3 rounded-xl bg-white/[0.05] border border-white/10 text-white/55 text-sm font-bold"
                >
                  Cancel
                </button>
                <button
                  onClick={() => decide(declining.req, "reject", {
                    reasonKey: declining.reasonKey,
                    note: declining.note.trim() || undefined,
                  })}
                  disabled={busyId === declining.req.id}
                  className="flex-1 py-3 rounded-xl bg-red-500/20 border border-red-400/40 text-red-200 text-sm font-bold disabled:opacity-40"
                >
                  {busyId === declining.req.id ? "Declining..." : "Send decline"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MOBILE PUG JOIN — Join raid as PUG, then direct to desktop for soft reserves
// ============================================================================
function MobilePugJoin({ raidId, token, me, onComplete, onCancel }: {
  raidId: string; token: string; me: Me; onComplete: () => void; onCancel: () => void;
}) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<any>(null);
  const [pugInfo, setPugInfo] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<FieldGroup[]>([]);
  const [charName, setCharName] = useState("");
  const [selectedClass, setSelectedClass] = useState<number | null>(null);
  const [selectedSpec, setSelectedSpec] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);
  const [useExisting, setUseExisting] = useState<string | null>(null);
  // Joined character info so soft reserves can be placed right here on mobile
  const [joinedCharId, setJoinedCharId] = useState<string | null>(null);
  const [joinedCharName, setJoinedCharName] = useState<string | null>(null);
  const [joinedClassName, setJoinedClassName] = useState<string | null>(null);
  const [showSR, setShowSR] = useState(false);

  // Load preview (no auth needed)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/raids/${encodeURIComponent(raidId)}/pug-preview?token=${encodeURIComponent(token)}`);
        if (res.ok) setPreview(await res.json());
      } catch (_) {}
    })();
  }, [raidId, token]);

  // Load pug-info when logged in
  useEffect(() => {
    if (!me) { setLoading(false); return; }
    (async () => {
      try {
        const res = await fetch(`${API_URL}/raids/${encodeURIComponent(raidId)}/pug-info?token=${encodeURIComponent(token)}`, { credentials: "include" });
        if (!res.ok) { setError("Invalid or expired PUG invite"); setLoading(false); return; }
        const data = await res.json();
        setPugInfo(data);

        // If already joined, skip to success
        if (data.existingPugCharacter) {
          setJoinedCharId(data.existingPugCharacter.character_id);
          setJoinedCharName(data.existingPugCharacter.character_name);
          setJoinedClassName(data.existingPugCharacter.class_name || null);
          setJoined(true);
          setLoading(false);
          return;
        }

        // Load game fields for class/spec selection
        if (data.gameId) {
          const fRes = await fetch(`${API_URL}/games/${data.gameId}/fields`, { credentials: "include" });
          if (fRes.ok) setFields(await fRes.json());
        }
      } catch (e: any) {
        setError(e.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [me, raidId, token]);

  const classGroup = fields.find(g => g.key === "class");
  const specGroup = fields.find(g => g.key === "spec");
  const selectedClassOpt = classGroup?.options.find(o => o.id === selectedClass);
  const selectedClassKey = selectedClassOpt?.key;

  const availableSpecs = specGroup?.options.filter(spec => {
    if (!spec.metadata?.allowed_class_keys) return true;
    if (!selectedClassKey) return true;
    return spec.metadata.allowed_class_keys.includes(selectedClassKey);
  }) || [];

  const handleJoin = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const body: any = { token };
      if (useExisting) {
        body.existingCharacterId = useExisting;
      } else {
        if (!charName.trim() || !selectedSpec) { setSubmitting(false); return; }
        body.characterName = charName.trim();
        body.specOptionId = selectedSpec;
        if (selectedClass) body.classOptionId = selectedClass;
      }

      const res = await fetch(`${API_URL}/raids/${encodeURIComponent(raidId)}/pug-join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body)
      });

      if (res.ok) {
        const data = await res.json().catch(() => ({} as any));
        setJoinedCharId(data.characterId || null);
        if (useExisting) {
          const existing = (pugInfo?.userCharacters || []).find((c: any) => c.id === useExisting);
          setJoinedCharName(existing?.name || null);
          setJoinedClassName(data.className || existing?.class_name || null);
        } else {
          setJoinedCharName(charName.trim());
          setJoinedClassName(data.className || null);
        }
        showToast("Joined raid!");
        setJoined(true);
      } else {
        const data = await res.json().catch(() => ({}));
        showToast(data.error || "Failed to join", "error");
      }
    } catch {
      showToast("Failed to join", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // --- LOGIN SCREEN ---
  if (!me) {
    return (
      <div className="fixed inset-0 bg-[#09090b] flex flex-col p-6">
        <button onClick={onCancel} className="self-start p-2 -ml-2 mb-4"><Icons.Back /></button>
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 rounded-2xl bg-cyan-500/20 flex items-center justify-center text-2xl mb-4">⚔</div>
          <h2 className="text-2xl font-bold mb-1">{preview?.raidName || "Raid"}</h2>
          <p className="text-white/50 text-sm mb-1">{preview?.guildName || ""}</p>
          {preview?.sessions?.[0] && (
            <p className="text-white/40 text-xs mb-6">
              {new Date(preview.sessions[0].starts_at).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
          <p className="text-white/50 mb-6">Sign in to join as a PUG</p>
          
          <a href={`${API_URL}/auth/discord`}
            onClick={() => sessionStorage.setItem("pending_pug_sr", JSON.stringify({ raidId, token }))}
            className="w-full max-w-xs py-4 bg-[#5865F2] rounded-xl font-bold text-lg flex items-center justify-center gap-3"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
            </svg>
            Sign in with Discord
          </a>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#09090b] flex items-center justify-center">
        <div className="text-white/60">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-[#09090b] flex flex-col items-center justify-center p-6">
        <div className="text-red-400 mb-4">{error}</div>
        <button onClick={onCancel} className="px-6 py-3 bg-white/10 rounded-lg font-bold">Go Back</button>
      </div>
    );
  }

  // --- SUCCESS SCREEN — Joined, pick soft reserves right here ---
  if (joined) {
    // Full soft reserve flow (same page desktop uses; it's responsive)
    if (showSR && joinedCharId) {
      return (
        <div className="fixed inset-0 z-50 bg-[#09090b] overflow-y-auto overscroll-contain text-white">
          <SoftReservePage
            raidId={raidId}
            raidInfo={{
              id: raidId,
              name: pugInfo?.raidName || "Raid",
              guild_id: "", // PUG doesn't need guild context for SR
              game_id: pugInfo?.gameId || "",
              raid_option_id: 0,
              raidName: pugInfo?.raidName || "Raid",
              hostedByGuild: pugInfo?.guildName || "",
              sessions: pugInfo?.sessions || undefined,
            }}
            canManage={false}
            myCharacters={[{ id: joinedCharId, name: joinedCharName || "My Character", class_name: joinedClassName }]}
            enrolledCharacterId={joinedCharId}
            onBack={() => setShowSR(false)}
          />
        </div>
      );
    }

    return (
      <div className="fixed inset-0 bg-[#09090b] flex flex-col items-center justify-center p-6 text-center">
        <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center text-4xl mb-4">✓</div>
        <h2 className="text-2xl font-bold mb-2">You're In!</h2>
        <p className="text-white/60 mb-6">
          You've joined <strong className="text-white">{pugInfo?.raidName || "the raid"}</strong> as a PUG.
        </p>

        {joinedCharId ? (
          <button
            onClick={() => setShowSR(true)}
            className="w-full max-w-xs py-4 mb-3 bg-amber-500/15 border border-amber-500/40 text-amber-300 rounded-xl font-bold text-lg flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
          >
            📋 Pick Soft Reserves
          </button>
        ) : (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6 max-w-sm">
            <div className="text-amber-400 font-bold text-sm mb-1">📋 Soft Reserves</div>
            <p className="text-white/60 text-sm leading-relaxed">
              Find this raid on your Home tab and tap <strong className="text-white">Soft Reserves</strong> to pick your items before the raid.
            </p>
          </div>
        )}

        <button
          onClick={onComplete}
          className="w-full max-w-xs py-4 bg-cyan-600 rounded-xl font-bold text-lg"
        >
          Done
        </button>
      </div>
    );
  }

  // --- JOIN FORM ---
  const existingChars: any[] = pugInfo?.userCharacters || [];

  return (
    <div className="fixed inset-0 bg-[#09090b] flex flex-col">
      <div className="flex-shrink-0 p-4 border-b border-white/10 flex items-center gap-3">
        <button onClick={onCancel} className="p-1 text-white/50"><Icons.Back /></button>
        <div>
          <div className="text-[10px] text-cyan-400 uppercase font-bold tracking-wider">PUG Join</div>
          <div className="font-bold text-white truncate">{pugInfo?.raidName || "Raid"} · {pugInfo?.guildName || ""}</div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-5" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 100px)' }}>
        {/* Use existing character? */}
        {existingChars.length > 0 && !useExisting && (
          <div>
            <h3 className="text-sm font-bold uppercase text-white/40 mb-2">Use Existing Character</h3>
            <div className="space-y-2">
              {existingChars.map((ch: any) => (
                <button
                  key={ch.id}
                  onClick={() => setUseExisting(ch.id)}
                  className={`w-full text-left p-3 rounded-xl border transition-all ${
                    useExisting === ch.id
                      ? "bg-cyan-600/20 border-cyan-500/50"
                      : "bg-white/5 border-white/10"
                  }`}
                >
                  <div className="font-bold text-white">{ch.name}</div>
                  <div className="text-xs text-white/50">{ch.class_name} • {ch.spec_label || ch.role_label || "No spec"}</div>
                </button>
              ))}
            </div>
            <div className="text-center text-white/30 text-xs my-3">or create a new character below</div>
          </div>
        )}

        {/* Create new character form */}
        {!useExisting && (
          <>
            <div>
              <label className="text-xs text-white/50 block mb-1.5 uppercase font-bold tracking-wider">Character Name</label>
              <input
                type="text"
                value={charName}
                onChange={(e) => setCharName(e.target.value)}
                maxLength={24}
                className="w-full bg-[#181a20] border border-white/20 rounded-xl px-4 py-3 text-white placeholder:text-white/20 focus:outline-none focus:border-cyan-500"
                placeholder="Enter character name"
              />
            </div>

            {classGroup && (
              <div>
                <label className="text-xs text-white/50 block mb-2 uppercase font-bold tracking-wider">Class</label>
                <div className="grid grid-cols-3 gap-2">
                  {classGroup.options.map((opt) => {
                    const isSelected = selectedClass === opt.id;
                    const color = opt.metadata?.color || "#fff";
                    return (
                      <button
                        key={opt.id}
                        onClick={() => { setSelectedClass(opt.id); setSelectedSpec(null); }}
                        className={`py-3 px-2 rounded-lg text-xs font-bold transition-all border ${
                          isSelected ? "shadow-lg scale-[1.02]" : "border-white/5 opacity-80"
                        }`}
                        style={{
                          backgroundColor: isSelected ? `${color}50` : `${color}15`,
                          borderColor: isSelected ? color : 'transparent',
                          color: color
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {specGroup && selectedClass && (
              <div>
                <label className="text-xs text-white/50 block mb-2 uppercase font-bold tracking-wider">Spec</label>
                <div className="flex flex-wrap gap-2">
                  {availableSpecs.map((spec) => {
                    const isSelected = selectedSpec === spec.id;
                    return (
                      <button
                        key={spec.id}
                        onClick={() => setSelectedSpec(spec.id)}
                        className={`px-4 py-2.5 rounded-lg text-sm font-bold transition-all border ${
                          isSelected
                            ? "bg-cyan-600 border-cyan-400 text-white shadow-lg shadow-cyan-900/50"
                            : "bg-white/5 border-white/10 text-white/60"
                        }`}
                      >
                        {spec.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* If using existing, show selected */}
        {useExisting && (
          <div className="bg-cyan-600/20 border border-cyan-500/50 rounded-xl p-4">
            <div className="text-xs text-cyan-400 font-bold uppercase mb-1">Selected Character</div>
            <div className="font-bold text-white">
              {existingChars.find((c: any) => c.id === useExisting)?.name || "Unknown"}
            </div>
            <button onClick={() => setUseExisting(null)} className="text-xs text-white/50 mt-1 underline">Change</button>
          </div>
        )}

        <button
          onClick={handleJoin}
          disabled={submitting || (!useExisting && (!charName.trim() || !selectedSpec))}
          className="w-full py-4 bg-green-600 rounded-xl font-bold text-lg text-white disabled:opacity-50 shadow-lg shadow-green-900/20"
        >
          {submitting ? "Joining..." : "Join Raid as PUG"}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// MOBILE INVITE WIZARD - Fixed Styling, Added Logo, Improved Visibility
// ============================================================================
function MobileInviteWizard({ code, me, onComplete, onCancel }: { code: string; me: Me; onComplete: () => void; onCancel: () => void }) {
  const { showToast } = useToast();
  const timezone = useTimezone();
  const [loading, setLoading] = useState(true);
  const [guild, setGuild] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState(me ? 1 : 0);
  
  // FIX: Auto-advance to Step 1 if user logs in while on Step 0
  useEffect(() => {
    if (me && step === 0) {
      setStep(1);
    }
  }, [me, step]);

  // Form state
  const [charName, setCharName] = useState("");
  const [fields, setFields] = useState<FieldGroup[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<Record<number, number | number[]>>({});
  const [availability, setAvailability] = useState<string>("0".repeat(336));
  const [submitting, setSubmitting] = useState(false);
  const [readyToRaid, setReadyToRaid] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        console.log("[MobileInviteWizard] Fetching guild info for code:", code);
        const gRes = await fetch(`${API_URL}/guilds/by-code/${code}`, { credentials: "include" });
        
        if (!gRes.ok) {
          const contentType = gRes.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            try {
              const errorData = await gRes.json();
              throw new Error(errorData.error || "Invalid invite");
            } catch (parseErr) {
              throw new Error(`Invalid invite (${gRes.status})`);
            }
          }
          throw new Error(`Invalid invite (${gRes.status})`);
        }
        
        let gData: any; 
        try {
          gData = await gRes.json();
        } catch (parseErr) {
          console.error("[MobileInviteWizard] JSON parse error:", parseErr);
          throw new Error("Failed to parse server response");
        }
        setGuild(gData);

        if (gData.game_id) {
          const fRes = await fetch(`${API_URL}/games/${gData.game_id}/fields`, { credentials: "include" });
          if (fRes.ok) {
            try {
              const fData = await fRes.json();
              setFields(fData);
              const presets: Record<number, number> = {};
              fData.forEach((group: FieldGroup) => {
                const matchVal = 
                  (group.key === "realm" && gData.server_name) ||
                  (group.key === "faction" && gData.faction) ||
                  (group.key === "region" && gData.region);
                if (matchVal) {
                  const opt = group.options.find(o => o.label.toLowerCase() === matchVal.toLowerCase());
                  if (opt) presets[group.id] = opt.id;
                }
              });
              setSelectedOptions(presets);
            } catch (e) {
              console.error("Failed to parse fields response:", e);
            }
          }
        }

        if (me) {
          const availRes = await fetch(`${API_URL}/me/availability`, { credentials: "include" });
          if (availRes.ok) {
            try {
              const availData = await availRes.json();
              const slots =
                availData.currentWeek?.slots ??
                availData.slots ??
                null;
              if (slots) setAvailability(slots);
            } catch (e) {
              console.error("Failed to parse availability response:", e);
            }
          }
        }
      } catch (e: any) {
        console.error("[MobileInviteWizard] Error:", e);
        setError(e.message || "An error occurred");
      } finally {
        setLoading(false);
      }
    })();
  }, [code, me]);

  // The week availability is saved to follows the character's region (preset
  // from the guild's), never the EU default: a US player joining on a Tuesday
  // after reset is already in the next week.
  const regionGroup = fields.find(g => g.key === "region");
  const regionOpt = regionGroup?.options.find(o => o.id === selectedOptions[regionGroup.id]);
  const inviteWeekStart = getCurrentResetWeek(resetRuleFrom(regionOpt?.metadata));

  const toggleDayPart = (dayIdx: number, part: MobileDayPart) => {
    setAvailability(toggleMobileDayPart(availability, inviteWeekStart, dayIdx, part, timezone));
  };

  const isPartActive = (dayIdx: number, part: MobileDayPart) =>
    isMobileDayPartActive(availability, inviteWeekStart, dayIdx, part, timezone);

  const handleSubmit = async () => {
    if (!charName.trim() || submitting) return;
    
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/me/availability`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        // applyToNextWeek: next week is pre-filled + confirmed with the same times
        body: JSON.stringify({ slots: availability, weekStart: inviteWeekStart, applyToNextWeek: true, confirm: true }),
        credentials: "include"
      });

      const res = await fetch(`${API_URL}/guilds/onboard`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          inviteCode: code,
          gameId: guild?.game_id,
          name: charName.trim(),
          options: selectedOptions
        })
      });

      if (res.ok) {
        showToast(`Welcome to ${guild?.name}!`);
        onComplete();
      } else {
        try {
          const contentType = res.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            const data = await res.json();
            showToast(data.error || "Failed to join", "error");
          } else {
            showToast(`Failed to join (${res.status})`, "error");
          }
        } catch {
          showToast(`Failed to join (${res.status})`, "error");
        }
      }
    } catch {
      showToast("Failed to join guild", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const classGroup = fields.find(g => g.key === "class");
  const specGroup = fields.find(g => g.key === "spec");
  
  const selectedClassId = classGroup ? selectedOptions[classGroup.id] : null;
  const selectedClassOpt = classGroup?.options.find(o => o.id === selectedClassId);
  const selectedClassKey = selectedClassOpt?.key;

  const availableSpecs = specGroup?.options.filter(spec => {
    if (!spec.metadata?.allowed_class_keys) return true;
    if (!selectedClassKey) return true;
    return spec.metadata.allowed_class_keys.includes(selectedClassKey);
  }) || [];

  const hasValidCharacter = charName.trim().length >= 2 && selectedClassId;
  const hasSpec = specGroup ? (
    specGroup.multiSelect
      ? (Array.isArray(selectedOptions[specGroup.id]) && (selectedOptions[specGroup.id] as number[]).length > 0)
      : Number(selectedOptions[specGroup.id]) > 0
  ) : true;

  // Helper to get logo URL
  const getLogoUrl = () => {
    if (!guild?.logoUrl) return null;
    return guild.logoUrl.startsWith("http") ? guild.logoUrl : `${API_URL}${guild.logoUrl}`;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#09090b] flex items-center justify-center">
        <div className="text-white/60">Loading...</div>
      </div>
    );
  }

  if (error || !guild) {
    return (
      <div className="fixed inset-0 bg-[#09090b] flex flex-col items-center justify-center p-6">
        <div className="text-red-400 mb-4">{error || "Invalid invite"}</div>
        <button onClick={onCancel} className="px-6 py-3 bg-white/10 rounded-lg font-bold">
          Go Back
        </button>
      </div>
    );
  }

  // Step 0: Login required
  if (!me) {
    return (
      <div className="fixed inset-0 bg-[#09090b] flex flex-col p-6">
        <button onClick={onCancel} className="self-start p-2 -ml-2 mb-4">
          <Icons.Back />
        </button>
        
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <div className="w-24 h-24 rounded-2xl bg-[#12141a] border border-white/10 flex items-center justify-center text-4xl font-bold text-cyan-400 mb-6 overflow-hidden shadow-2xl">
            {getLogoUrl() ? (
              <img src={getLogoUrl()!} alt={guild.name} className="w-full h-full object-cover" />
            ) : (
              guild?.tag || guild?.name?.[0] || "?"
            )}
          </div>
          <h2 className="text-2xl font-bold mb-2">{guild?.name || "Unknown Guild"}</h2>
          <p className="text-white/50 mb-8">Sign in to join this guild</p>
          
          <a 
            href={`${API_URL}/auth/discord`}
            onClick={() => sessionStorage.setItem("pending_invite_code", code)}
            className="w-full max-w-xs py-4 bg-[#5865F2] rounded-xl font-bold text-lg flex items-center justify-center gap-3 hover:bg-[#4752c4] transition-colors"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
            </svg>
            Sign in with Discord
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#09090b] flex flex-col">
      {/* Header with Logo */}
      <div className="flex-shrink-0 p-4 border-b border-white/10 flex items-center gap-3 bg-[#0e1015]">
        <button onClick={onCancel} className="p-1 text-white/50 hover:text-white">
          <Icons.Back />
        </button>
        
        {/* Guild Logo */}
        <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 overflow-hidden flex-shrink-0 flex items-center justify-center">
          {getLogoUrl() ? (
            <img src={getLogoUrl()!} alt={guild.name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-sm font-bold text-white/40">{guild?.name?.[0]}</span>
          )}
        </div>

        <div className="min-w-0">
          <div className="text-[10px] text-cyan-400 uppercase font-bold tracking-wider">Join Guild</div>
          <div className="font-bold text-white truncate">{guild?.name || "Unknown Guild"}</div>
        </div>
      </div>

      {/* Progress */}
      <div className="flex-shrink-0 p-4 border-b border-white/10">
        <div className="flex gap-2">
          {[1, 2, 3].map((s) => (
            <div 
              key={s}
              className={`flex-1 h-1 rounded-full transition-colors ${
                (step || 1) >= s ? "bg-cyan-500" : "bg-white/10"
              }`}
            />
          ))}
        </div>
        <div className="text-xs text-white/50 mt-2">
          Step {step || 1} of 3: {step === 1 ? "Character" : step === 2 ? "Availability" : step === 3 ? "Confirm" : "Loading..."}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 100px)' }}>
        {/* Step 1: Character */}
        {step === 1 && (
          <div className="space-y-5">
            <h3 className="text-lg font-bold text-white">Create Your Character</h3>
            
            {/* Name */}
            <div>
              <label className="text-xs text-white/50 block mb-1.5 uppercase font-bold tracking-wider">Character Name *</label>
              <input
                type="text"
                value={charName}
                onChange={(e) => setCharName(e.target.value)}
                maxLength={24}
                className="w-full bg-[#181a20] border border-white/20 rounded-xl px-4 py-3 text-white placeholder:text-white/20 focus:outline-none focus:border-cyan-500 transition-colors"
                placeholder="Enter character name"
              />
            </div>

            {/* Class */}
            {classGroup && (
              <div>
                <label className="text-xs text-white/50 block mb-2 uppercase font-bold tracking-wider">Class *</label>
                <div className="grid grid-cols-3 gap-2">
                  {classGroup.options.map((opt) => {
                    const isSelected = selectedOptions[classGroup.id] === opt.id;
                    const color = opt.metadata?.color || "#fff";
                    return (
                      <button
                        key={opt.id}
                        onClick={() => {
                          const newOpts = { ...selectedOptions, [classGroup.id]: opt.id };
                          if (specGroup?.id) delete newOpts[specGroup.id];
                          setSelectedOptions(newOpts);
                        }}
                        className={`py-3 px-2 rounded-lg text-xs font-bold transition-all border ${
                          isSelected
                            ? "shadow-lg scale-[1.02]"
                            : "border-white/5 hover:bg-white/10 opacity-80"
                        }`}
                        style={{ 
                          // FIX: Increased opacity for selected state to make it pop, lighter opacity for unselected
                          backgroundColor: isSelected ? `${color}50` : `${color}15`,
                          borderColor: isSelected ? color : 'transparent',
                          color: color
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Spec */}
            {specGroup && selectedClassId && (
              <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                <label className="text-xs text-white/50 block mb-2 uppercase font-bold tracking-wider">Spec *</label>
                <div className="flex flex-wrap gap-2">
                  {availableSpecs.map((spec) => {
                    const rawVal = selectedOptions[specGroup.id];
                    const valAsArray = Array.isArray(rawVal) ? rawVal : [];
                    const isSelected = specGroup.multiSelect
                      ? valAsArray.includes(spec.id)
                      : rawVal === spec.id;

                    return (
                      <button
                        key={spec.id}
                        onClick={() => {
                          if (specGroup.multiSelect) {
                            const current = Array.isArray(selectedOptions[specGroup.id])
                              ? (selectedOptions[specGroup.id] as number[])
                              : [];
                            const newVal = isSelected
                              ? current.filter(id => id !== spec.id)
                              : [...current, spec.id];
                            setSelectedOptions({ ...selectedOptions, [specGroup.id]: newVal });
                          } else {
                            setSelectedOptions({ ...selectedOptions, [specGroup.id]: spec.id });
                          }
                        }}
                        className={`px-4 py-2.5 rounded-lg text-sm font-bold transition-all border ${
                          isSelected
                            ? "bg-cyan-600 border-cyan-400 text-white shadow-lg shadow-cyan-900/50"
                            : "bg-white/5 border-white/10 text-white/60 hover:text-white hover:bg-white/10"
                        }`}
                      >
                        {spec.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Ready to Raid + Raid Interest */}
            {(() => {
                const raidInterestGroup = fields.find(g => g.key === "raid_interest" || g.key === "raid_preferences");
                if (!raidInterestGroup || !selectedClassId) return null;
                
                return (
                  <div className="space-y-3 pt-3 border-t border-white/10 mt-2">
                    <div className="flex items-center gap-3 cursor-pointer"
                      onClick={() => {
                        if (readyToRaid) {
                          setReadyToRaid(false);
                          setSelectedOptions(p => { const n = {...p}; delete n[raidInterestGroup.id]; return n; });
                        } else {
                          setReadyToRaid(true);
                        }
                      }}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                        readyToRaid ? "bg-emerald-500 border-emerald-400" : "border-white/30"
                      }`}>
                        {readyToRaid && (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <div>
                        <span className="text-sm font-bold text-white/80">Ready to Raid</span>
                        <p className="text-[10px] text-white/40">Select if this character is raid-ready</p>
                      </div>
                    </div>
                    
                    {readyToRaid && (
                    <div className="flex flex-wrap gap-2 pl-8">
                      {raidInterestGroup.options.map((opt: any) => {
                        const isSelected = Array.isArray(selectedOptions[raidInterestGroup.id])
                          ? (selectedOptions[raidInterestGroup.id] as number[]).includes(opt.id)
                          : selectedOptions[raidInterestGroup.id] === opt.id;
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => {
                              const prev = (selectedOptions[raidInterestGroup.id] as number[]) || [];
                              const next = isSelected ? prev.filter(x => x !== opt.id) : [...prev, opt.id];
                              setSelectedOptions(p => ({...p, [raidInterestGroup.id]: next}));
                            }}
                            className={`px-3 py-2 rounded-lg text-xs font-bold transition-all border ${
                              isSelected 
                                ? "bg-emerald-500/20 border-emerald-500 text-emerald-200" 
                                : "bg-white/5 border-white/10 text-white/40"
                            }`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                    )}
                  </div>
                );
            })()}

            <button
              onClick={() => setStep(2)}
              disabled={!hasValidCharacter || !hasSpec}
              className="w-full mt-4 py-4 bg-cyan-600 rounded-xl font-bold text-lg text-white disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-cyan-900/20"
            >
              Next: Set Availability
            </button>
          </div>
        )}

        {/* Step 2: Availability */}
        {step === 2 && (
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-white">Your Availability</h3>
            <p className="text-sm text-white/50">When can you typically raid? This helps with scheduling.</p>
            <p className="text-xs text-cyan-300/80">Confirmed for this week and next week automatically. You can change either week later.</p>
            
            {MOBILE_AVAIL_DAYS.map((day, dIdx) => (
              <div key={day} className="bg-[#12141a] rounded-xl border border-white/10 overflow-hidden">
                <div className="px-4 py-2 bg-white/5 font-bold text-white/60 text-xs uppercase">{day}</div>
                <div className="p-2 grid grid-cols-3 gap-2">
                  {MOBILE_DAY_PARTS.map((part) => (
                    <button
                      key={part.label}
                      onClick={() => toggleDayPart(dIdx, part)}
                      className={`py-3 px-2 rounded-lg text-center transition-all ${
                        isPartActive(dIdx, part)
                          ? "bg-cyan-600 text-white shadow-lg shadow-cyan-900/20"
                          : "bg-black/30 text-white/40 border border-white/5"
                      }`}
                    >
                      <div className="text-xs font-bold">{part.label}</div>
                      <div className="text-[10px] opacity-60">{part.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex gap-3 mt-4">
              <button onClick={() => setStep(1)} className="flex-1 py-3 bg-white/10 rounded-xl font-bold text-white hover:bg-white/20 transition-colors">
                Back
              </button>
              <button onClick={() => setStep(3)} className="flex-1 py-3 bg-cyan-600 rounded-xl font-bold text-white hover:bg-cyan-500 transition-colors shadow-lg shadow-cyan-900/20">
                Next: Confirm
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Confirm */}
        {step === 3 && (
          <div className="space-y-5">
            <h3 className="text-lg font-bold text-white">Confirm & Join</h3>
            
            <div className="bg-[#12141a] rounded-xl p-4 border border-white/10 space-y-3">
              <div className="flex justify-between items-center pb-3 border-b border-white/5">
                <span className="text-white/50 text-sm">Character</span>
                <span className="font-bold text-white text-lg">{charName}</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-white/5">
                <span className="text-white/50 text-sm">Class</span>
                <span className="font-bold" style={{ color: selectedClassOpt?.metadata?.color || "#fff" }}>
                  {selectedClassOpt?.label || "Not selected"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-white/50 text-sm">Guild</span>
                <div className="flex items-center gap-2">
                  {getLogoUrl() && <img src={getLogoUrl()!} className="w-5 h-5 rounded-full object-cover" />}
                  <span className="font-bold text-white">{guild?.name}</span>
                </div>
              </div>
            </div>

            <p className="text-sm text-white/50 leading-relaxed">
              You're about to join <strong className="text-white">{guild?.name}</strong> with your character <strong className="text-cyan-400">{charName}</strong>.
            </p>

            <div className="flex gap-3 mt-4">
              <button onClick={() => setStep(2)} className="flex-1 py-3 bg-white/10 rounded-xl font-bold text-white hover:bg-white/20 transition-colors">
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 py-4 bg-green-600 rounded-xl font-bold text-lg text-white disabled:opacity-50 hover:bg-green-500 transition-colors shadow-lg shadow-green-900/20"
              >
                {submitting ? "Joining..." : "Join Guild"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN MOBILE APP EXPORT
// ============================================================================
export default function MobileApp({ me, onLogout, inviteCode, onInviteComplete, pugSrData, onPugComplete, joinRequestToken, onJoinRequestComplete }: {
  me: Me;
  onLogout: () => void;
  inviteCode?: string | null;
  onInviteComplete?: () => void;
  pugSrData?: { raidId: string; token: string } | null;
  onPugComplete?: () => void;
  joinRequestToken?: string | null;
  onJoinRequestComplete?: () => void;
}) {
  const [activeTab, setActiveTab] = useState<Tab>("home");
  const [characters, setCharacters] = useState<Character[]>([]);
  const [raids, setRaids] = useState<ScheduledRaid[]>([]);
  const [fieldsByGame, setFieldsByGame] = useState<FieldsByGame>({});
  const [gameNames, setGameNames] = useState<Record<string, string>>({});
  const [officerGuilds, setOfficerGuilds] = useState<OfficerGuild[]>([]);
  const [pendingCounts, setPendingCounts] = useState<Record<string, number>>({});
  const [officerVersion, setOfficerVersion] = useState(0);
  const [showInviteWizard, setShowInviteWizard] = useState(!!inviteCode);
  const [showPugJoin, setShowPugJoin] = useState(!!pugSrData);
  const [showJoinRequest, setShowJoinRequest] = useState(!!joinRequestToken);
  const [dataVersion, setDataVersion] = useState(0);

  // Check for pending Discord join-request finalize token
  useEffect(() => {
    if (joinRequestToken || sessionStorage.getItem("pending_join_request_token")) {
      setShowJoinRequest(true);
    }
  }, [me, joinRequestToken]);

  // Check for pending invite
  useEffect(() => {
    const pending = sessionStorage.getItem("pending_invite_code");
    if (pending && me) {
      setShowInviteWizard(true);
    }
  }, [me]);

  // Check for pending PUG SR
  useEffect(() => {
    const pending = sessionStorage.getItem("pending_pug_sr");
    if (pending && me) {
      setShowPugJoin(true);
    } else if (pugSrData) {
      setShowPugJoin(true);
    } else if (window.location.hash.replace("#", "").startsWith("pug/")) {
      setShowPugJoin(true);
    }
  }, [me, pugSrData]);

  // Fetch data
  useEffect(() => {
    if (!me) {
      setCharacters([]);
      setRaids([]);
      return;
    }

    // Fetch characters
    fetch("/characters", { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(data => setCharacters(Array.isArray(data) ? data : []))
      .catch(console.error);

    // Fetch raids
    fetch("/my/raids", { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then((rows: any[]) => {
        const mapped = rows.map(row => {
          // Earliest session start + latest session end (start + duration, default 3h).
          // sessions come back unordered, so don't trust sessions[0].
          const sessions: any[] = Array.isArray(row.sessions) ? row.sessions : [];
          let startMs = Infinity;
          let endMs = 0;
          for (const s of sessions) {
            const start = new Date(s.starts_at).getTime();
            if (Number.isNaN(start)) continue;
            startMs = Math.min(startMs, start);
            const durMs = (Number(s.duration) > 0 ? Number(s.duration) : 180) * 60 * 1000;
            endMs = Math.max(endMs, start + durMs);
          }
          const startsAt = Number.isFinite(startMs) ? new Date(startMs).toISOString() : null;
          const endsAt = endMs > 0 ? new Date(endMs).toISOString() : null;

          let localTimeLabel = "TBD";
          if (startsAt) {
            const d = new Date(startsAt);
            localTimeLabel = d.toLocaleDateString(undefined, { weekday: 'short', month: 'numeric', day: 'numeric' }) +
              ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
          }
          return {
            id: String(row.id),
            raidName: row.raid_name || row.raid_label || "Unknown",
            gameId: row.game_id ? String(row.game_id) : null,
            gameName: shortGameName(row.game_name) || null,
            localTimeLabel,
            hostedByGuild: row.guild_tag || row.guild_name || "Unknown",
            myCharName: row.my_assignment?.name,
            myCharClass: row.my_assignment?.class_name,
            raidImageUrl: row.raid_image_url || null,
            startsAt,
            endsAt,
            softReserveInfo: row.soft_reserve_info || null,
          };
        });
        setRaids(mapped);
      })
      .catch(console.error);

    // Every game's class/spec table, so each character and raid uses its own.
    fetch("/games", { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(async (games: any[]) => {
        const list = Array.isArray(games) ? games : [];
        setGameNames(Object.fromEntries(list.map(g => [String(g.id), g.name])));
        const entries = await Promise.all(list.map(async g => {
          try {
            const fRes = await fetch(`/games/${g.id}/fields`, { credentials: "include" });
            return [String(g.id), fRes.ok ? await fRes.json() : []] as const;
          } catch {
            return [String(g.id), []] as const;
          }
        }));
        setFieldsByGame(Object.fromEntries(entries));
      })
      .catch(console.error);
  }, [me, dataVersion]);

  // Guilds this user can administer, plus how many applications are waiting in
  // each. Drives whether the Officer tab exists at all and the nav badge, so it
  // refreshes on its own version counter after an approve/decline.
  useEffect(() => {
    if (!me) {
      setOfficerGuilds([]);
      setPendingCounts({});
      return;
    }
    let alive = true;
    fetch("/guilds", { credentials: "include" })
      .then(r => (r.ok ? r.json() : []))
      .then(async (rows: any[]) => {
        if (!alive) return;
        const mine: OfficerGuild[] = (Array.isArray(rows) ? rows : [])
          .filter(g => g.isOwner || g.isOfficer)
          .map(g => ({
            id: String(g.id),
            name: g.name,
            tag: g.tag || null,
            logoUrl: g.logoUrl || null,
            isOwner: !!g.isOwner,
          }));
        setOfficerGuilds(mine);
        if (mine.length === 0) { setPendingCounts({}); return; }

        // Counts only. The Officer tab refetches the full rows for whichever
        // guild is selected.
        const counts = await Promise.all(mine.map(async g => {
          try {
            const res = await fetch(`/guilds/${g.id}/join-requests`, { credentials: "include" });
            if (!res.ok) return [g.id, 0] as const;
            const list = await res.json();
            return [g.id, Array.isArray(list) ? list.length : 0] as const;
          } catch {
            return [g.id, 0] as const;
          }
        }));
        if (alive) setPendingCounts(Object.fromEntries(counts));
      })
      .catch(console.error);
    return () => { alive = false; };
  }, [me, officerVersion]);

  // An officer who loses their last officer role while sitting on the tab
  // should not be stranded on a tab that no longer renders.
  useEffect(() => {
    if (activeTab === "officer" && officerGuilds.length === 0) setActiveTab("home");
  }, [activeTab, officerGuilds]);

  const refreshCharacters = () => {
    setDataVersion(v => v + 1);
  };

  const isOfficer = officerGuilds.length > 0;
  const totalPending = Object.values(pendingCounts).reduce((a, b) => a + b, 0);

  // --- PUG Join Flow ---
  const actualPugData = pugSrData || (() => {
    const pending = sessionStorage.getItem("pending_pug_sr");
    if (pending) { try { return JSON.parse(pending); } catch (_) {} }
    // Also check URL hash
    const hash = window.location.hash.replace("#", "");
    if (hash.startsWith("PugSR")) {
      const params = new URLSearchParams(hash.split("?")[1]);
      const raidId = params.get("raid");
      const token = params.get("token");
      if (raidId && token) return { raidId, token };
    }
    return null;
  })();

  if (showPugJoin && actualPugData) {
    return (
      <MobilePugJoin
        raidId={actualPugData.raidId}
        token={actualPugData.token}
        me={me}
        onComplete={() => {
          setShowPugJoin(false);
          sessionStorage.removeItem("pending_pug_sr");
          onPugComplete?.();
          refreshCharacters();
        }}
        onCancel={() => {
          setShowPugJoin(false);
          sessionStorage.removeItem("pending_pug_sr");
        }}
      />
    );
  }

  // --- Discord Join Request Finalize Flow ---
  const actualJoinToken = joinRequestToken || sessionStorage.getItem("pending_join_request_token");
  if (showJoinRequest && actualJoinToken) {
    return (
      <TimezoneProvider>
        <div className="fixed inset-0 bg-[#09090b] text-white overflow-y-auto">
          <JoinRequestPage
            token={actualJoinToken}
            me={me}
            onDone={() => {
              setShowJoinRequest(false);
              sessionStorage.removeItem("pending_join_request_token");
              onJoinRequestComplete?.();
              refreshCharacters();
            }}
          />
        </div>
      </TimezoneProvider>
    );
  }

  const actualInviteCode = inviteCode || sessionStorage.getItem("pending_invite_code");

  if (showInviteWizard && actualInviteCode) {
    return (
      <MobileInviteWizard
      
        code={actualInviteCode}
        me={me}
        onComplete={() => {
          setShowInviteWizard(false);
          sessionStorage.removeItem("pending_invite_code");
          onInviteComplete?.();
          refreshCharacters();
        }}
        onCancel={() => {
          setShowInviteWizard(false);
          sessionStorage.removeItem("pending_invite_code");
        }}
      />
    );
  }


  return (
    <TimezoneProvider>
      <div className="fixed inset-0 bg-[#09090b] text-white overflow-hidden flex flex-col">
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
        {activeTab === "home" && <MobileHome me={me} raids={raids} characters={characters} fieldsByGame={fieldsByGame} />}
          {activeTab === "schedule" && <MobileSchedule me={me} />}
          {activeTab === "chat" && <MobileChat me={me} />}
          {activeTab === "officer" && isOfficer && (
            <MobileOfficer
              guilds={officerGuilds}
              fieldsByGame={fieldsByGame}
              pendingCounts={pendingCounts}
              onRequestsChanged={() => setOfficerVersion(v => v + 1)}
            />
          )}
          {activeTab === "profile" && (
            <MobileProfile 
              me={me} 
              characters={characters} 
              fieldsByGame={fieldsByGame} 
              gameNames={gameNames}
              onLogout={onLogout}
              onCharactersChange={refreshCharacters}
            />
          )}
        </div>

        <div className="flex-shrink-0 h-[65px] bg-[#121214]/95 backdrop-blur-lg border-t border-white/10 flex items-center justify-around px-2"
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          {((isOfficer
            ? ["home", "schedule", "chat", "officer", "profile"]
            : ["home", "schedule", "chat", "profile"]) as Tab[]).map((t) => {
            const IconComponent = Icons[t.charAt(0).toUpperCase() + t.slice(1) as keyof typeof Icons];
            const active = activeTab === t;
            const badge = t === "officer" ? totalPending : 0;
            return (
              <button
                key={t}
                onClick={() => setActiveTab(t)}
                className={`relative flex flex-col items-center gap-1 ${isOfficer ? "w-14" : "w-16"} py-2 transition-colors ${active ? "text-cyan-400" : "text-gray-500 active:text-gray-300"}`}
              >
                {/* active top indicator */}
                <span className={`absolute top-0 left-1/2 -translate-x-1/2 h-0.5 rounded-full bg-cyan-400 transition-all duration-300 ${active ? "w-8 opacity-100 shadow-[0_0_8px_rgba(34,211,238,0.6)]" : "w-0 opacity-0"}`} />
                <div className={`relative transition-transform duration-300 ${active ? "scale-110 -translate-y-0.5 drop-shadow-[0_0_6px_rgba(34,211,238,0.35)]" : ""}`}>
                  <IconComponent />
                  {badge > 0 && (
                    <span className="absolute -top-1 -right-1.5 inline-flex items-center justify-center min-w-[15px] h-[15px] px-1 rounded-full bg-amber-500 text-[9px] font-black text-black ring-2 ring-[#121214]">
                      {badge > 9 ? "9+" : badge}
                    </span>
                  )}
                </div>
                <span className="text-[10px] uppercase font-bold tracking-wide">{t}</span>
              </button>
            );
          })}
        </div>
      </div>
    </TimezoneProvider>
  );
}